import React, { useEffect, useState, useCallback } from 'react';
import Plotly from 'plotly.js/dist/plotly';
import Plot from './PlotlyWrapper';
import { useDashboard } from '../../context/DashboardContext';
import DateRangeSelector from './DateRangeSelector';
import { 
  fetchCityLevelWeatherData, 
  fetchWeatherDataForWard,
  fetchPowerOutageTrendData,
  fetchWeatherForDate,
  fetchSeverityDataForDateRange,
  fetchGroundwaterTrendData,
  fetchGroundwaterSeverityDataForDateRange,
  fetchInformalWorkersTrendData,
  fetchInformalWorkersSeverityDataForDateRange,
  fetchConstructionLaborTrendData,
  fetchConstructionLaborSeverityDataForDateRange,
  fetchTransportDisruptionsTrendData,
  fetchTransportDisruptionsDataForWards,
  fetchRoadClosuresDataForWards,
  fetchTrafficSignalFailuresDataForWards,
  fetchCoolingSystemFailuresDataForWards,
  fetchSchoolClosuresDataForWards,
  fetchSchoolCoolingDataForWards,
  fetchWaterSupplyDataForWards,
  fetchWaterTankersDataForWards,
  fetchHeatDifferentialDataForWards,
  fetchSurfaceTemperatureDataForWards,
  fetchShelterOccupancyDataForWards,
  fetchCoolingSystemFailuresTrendData,
  fetchSchoolClosuresTrendData,
  fetchSchoolCoolingTrendData,
  fetchWaterSupplyTrendData,
  fetchWaterTankersTrendData,
  fetchHeatDifferentialTrendData,
  fetchSurfaceTemperatureTrendData,
  fetchShelterOccupancyTrendData
} from '../../services/api';
import { 
  calculateSeverityLevel, 
  calculateGroundwaterSeverityLevel, 
  calculateInformalWorkersSeverityLevel,
  calculateLostLaborHoursSeverityLevel,
  calculateNoShadeWaterSeverityLevel,
  calculateHeatIllnessSeverityLevel
} from '../../utils/severityUtils';

const transportDisruptionsIndicatorName = 'Public transport disruptions (rail, metro, bus)';
const INDICATOR_NAMES = {
  roadClosures: 'Road/bridge degradation or closures',
  trafficSignal: 'Traffic signal / equipment failures',
  coolingSystem: 'Cooling system failure in public buildings',
  schoolClosures: 'No. of school/college closure days due to heat',
  schoolCooling: '% of schools without basic cooling infrastructure',
  waterSupply: 'Drop in per capita water availability (lpcd)',
  waterTankers: 'Percentage of wards dependent on water tankers',
  heatDifferential: 'Temperature differential (urban vs. rural)',
  surfaceTemperature: 'Surface temperature & low vegetation zones',
  shelterOccupancy: 'Homeless and street dwellers exposed'
};

// Parameter tab labels for temperature-only trend chart title (indicator param tabs)
const PARAM_TAB_LABELS = {
  coolingSystem: { total_people_affected: 'Total People Affected', total_cooling_capacity_affected: 'Total Cooling Capacity Affected' },
  schoolClosures: { school_closures_severity_ward_wise: 'School Closures Severity Ward Wise', total_schools_in_ward: 'Total Schools in Ward' },
  schoolCooling: { classrooms_with_cooling_infra: 'Classrooms with Cooling Infra', total_classrooms_in_ward: 'Total Classrooms in Ward' },
  waterSupply: { daily_demand_lpcd: 'Daily Demand (lpcd)', daily_supply_lpcd: 'Daily Supply (lpcd)' },
  waterTankers: { tanker_trips: 'Tanker Trips', emergency_requests: 'Emergency Requests' },
  heatDifferential: { avg_urban_temp_c: 'Avg Urban Temp (°C)', avg_rural_temp_c: 'Avg Rural Temp (°C)' },
  surfaceTemperature: { surface_temp_c: 'Surface Temp (°C)', land_use_type: 'Land Use Type' },
  shelterOccupancy: { occupancy_rate: 'Occupancy Rate (%)' }
};

// Trend API + field for parameter tabs (so we can show bar chart when data is available)
const PARAM_TREND_CONFIG = {
  coolingSystem: {
    total_people_affected: { fetchFn: null, trendField: 'total_people_affected', barTitle: 'Total People Affected', y2Label: 'Total People Affected' },
    total_cooling_capacity_affected: { fetchFn: null, trendField: 'total_cooling_capacity_affected', barTitle: 'Total Cooling Capacity Affected', y2Label: 'Cooling Capacity (min)' }
  },
  schoolClosures: {
    school_closures_severity_ward_wise: { fetchFn: null, trendField: 'school_closures_severity_ward_wise', barTitle: 'School Closures Severity Ward Wise', y2Label: 'School Closures Severity Ward Wise' },
    total_schools_in_ward: { fetchFn: null, trendField: 'total_schools_in_ward', barTitle: 'Total Schools in Ward', y2Label: 'Total Schools in Ward' }
  },
  schoolCooling: {
    classrooms_with_cooling_infra: { fetchFn: null, trendField: 'classrooms_with_cooling_infra', barTitle: 'Classrooms with Cooling Infra', y2Label: 'Classrooms with Cooling Infra' },
    total_classrooms_in_ward: { fetchFn: null, trendField: 'total_classrooms_in_ward', barTitle: 'Total Classrooms in Ward', y2Label: 'Total Classrooms in Ward' }
  },
  waterSupply: {
    daily_demand_lpcd: { fetchFn: null, trendField: 'daily_demand_lpcd', barTitle: 'Daily Demand (lpcd)', y2Label: 'Daily Demand (lpcd)' },
    daily_supply_lpcd: { fetchFn: null, trendField: 'daily_supply_lpcd', barTitle: 'Daily Supply (lpcd)', y2Label: 'Daily Supply (lpcd)' }
  },
  waterTankers: {
    tanker_trips: { fetchFn: null, trendField: 'tanker_trips', barTitle: 'Tanker Trips', y2Label: 'Tanker Trips' },
    emergency_requests: { fetchFn: null, trendField: 'emergency_requests', barTitle: 'Emergency Requests', y2Label: 'Emergency Requests' }
  },
  heatDifferential: {
    avg_urban_temp_c: { fetchFn: null, trendField: 'avg_urban_temp_c', barTitle: 'Avg Urban Temp (°C)', y2Label: 'Avg Urban Temp (°C)' },
    avg_rural_temp_c: { fetchFn: null, trendField: 'avg_rural_temp_c', barTitle: 'Avg Rural Temp (°C)', y2Label: 'Avg Rural Temp (°C)' }
  },
  surfaceTemperature: {
    surface_temp_c: { fetchFn: null, trendField: 'surface_temp_c', barTitle: 'Surface Temp (°C)', y2Label: 'Surface Temp (°C)' },
    land_use_type: { fetchFn: null, trendField: 'land_use_type', barTitle: 'Land Use Type', y2Label: 'Land Use Type' }
  },
  shelterOccupancy: {
    occupancy_rate: { fetchFn: null, trendField: 'occupancy_rate', barTitle: 'Occupancy Rate (%)', y2Label: 'Occupancy Rate (%)' }
  }
};
// Wire fetch functions (cannot reference in static object above)
PARAM_TREND_CONFIG.coolingSystem.total_people_affected.fetchFn = fetchCoolingSystemFailuresTrendData;
PARAM_TREND_CONFIG.coolingSystem.total_cooling_capacity_affected.fetchFn = fetchCoolingSystemFailuresTrendData;
PARAM_TREND_CONFIG.schoolClosures.school_closures_severity_ward_wise.fetchFn = fetchSchoolClosuresTrendData;
PARAM_TREND_CONFIG.schoolClosures.total_schools_in_ward.fetchFn = fetchSchoolClosuresTrendData;
PARAM_TREND_CONFIG.schoolCooling.classrooms_with_cooling_infra.fetchFn = fetchSchoolCoolingTrendData;
PARAM_TREND_CONFIG.schoolCooling.total_classrooms_in_ward.fetchFn = fetchSchoolCoolingTrendData;
PARAM_TREND_CONFIG.waterSupply.daily_demand_lpcd.fetchFn = fetchWaterSupplyTrendData;
PARAM_TREND_CONFIG.waterSupply.daily_supply_lpcd.fetchFn = fetchWaterSupplyTrendData;
PARAM_TREND_CONFIG.waterTankers.tanker_trips.fetchFn = fetchWaterTankersTrendData;
PARAM_TREND_CONFIG.waterTankers.emergency_requests.fetchFn = fetchWaterTankersTrendData;
PARAM_TREND_CONFIG.heatDifferential.avg_urban_temp_c.fetchFn = fetchHeatDifferentialTrendData;
PARAM_TREND_CONFIG.heatDifferential.avg_rural_temp_c.fetchFn = fetchHeatDifferentialTrendData;
PARAM_TREND_CONFIG.surfaceTemperature.surface_temp_c.fetchFn = fetchSurfaceTemperatureTrendData;
PARAM_TREND_CONFIG.surfaceTemperature.land_use_type.fetchFn = fetchSurfaceTemperatureTrendData;
PARAM_TREND_CONFIG.shelterOccupancy.occupancy_rate.fetchFn = fetchShelterOccupancyTrendData;

const TrendContainer = ({ 
  indicatorName, 
  city, 
  isPowerOutage, 
  powerOutageMode,
  isGroundwater,
  groundwaterMode,
  isInformalWorkers,
  informalWorkersMode,
  isConstructionLabor,
  constructionLaborMode,
  isTransportDisruptions,
  transportDisruptionsMode,
  isRoadClosures,
  roadClosuresMode,
  isTrafficSignalFailures,
  trafficSignalFailuresMode,
  isCoolingSystemFailures,
  coolingSystemFailuresMode,
  isSchoolClosures,
  schoolClosuresMode,
  isSchoolCooling,
  schoolCoolingMode,
  isWaterSupply,
  waterSupplyMode,
  isWaterTankers,
  waterTankersMode,
  isHeatDifferential,
  heatDifferentialMode,
  isSurfaceTemperature,
  surfaceTemperatureMode,
  isShelterOccupancy,
  shelterOccupancyMode,
  selectedDate,
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
  availableMonths
}) => {
  const {
    selectedWard,
    setSelectedWard,
    weatherData,
    setWeatherData,
    getIndicatorThreshold
  } = useDashboard();

  const [plotData, setPlotData] = useState([]);
  const [plotLayout, setPlotLayout] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  // Initialize with empty data to prevent null errors
  const emptySeverityData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
  const [currentMonthData, setCurrentMonthData] = useState(emptySeverityData);
  const [previousYearData, setPreviousYearData] = useState(emptySeverityData);
  
  // Track threshold to trigger re-render when it changes
  const indicatorNameForThreshold = 'Power outages (duration and geographic scope)';
  const groundwaterIndicatorName = 'Groundwater stress levels';
  const currentThreshold = getIndicatorThreshold(indicatorNameForThreshold);
  const groundwaterThreshold = getIndicatorThreshold(groundwaterIndicatorName);

  // Reset ward selection
  const handleResetWard = () => {
    setSelectedWard(null);
    setWeatherData(null);
  };

  // Fetch severity data for a specific month
  const fetchSeverityData = useCallback(async (date) => {
    // Always get fresh threshold value
    const indicatorName = 'Power outages (duration and geographic scope)';
    const threshold = getIndicatorThreshold(indicatorName);
    console.log(`Fetching severity data with threshold: ${threshold}`);
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const start = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

      const result = await fetchSeverityDataForDateRange(start, end, city);
      
      const severityCounts = { LOW: 0, MEDIUM: 0, HIGH: 0 };
      const wardSeverityMap = {};

      if (result.ward_data && result.ward_data.length > 0) {
        result.ward_data.forEach(record => {
          const ward = record.ward_number;
          if (!wardSeverityMap[ward]) {
            wardSeverityMap[ward] = { LOW: 0, MEDIUM: 0, HIGH: 0 };
          }
          
          // Recalculate severity if severity_index is available
          let severityLevel = record.severity_level;
          if (record.severity_index !== undefined && record.severity_index !== null) {
            severityLevel = calculateSeverityLevel(record.severity_index, threshold);
          }

          // Fold CRITICAL into HIGH for three-level display
          const normalizedLevel = String(severityLevel).toUpperCase();
          const effectiveLevel = normalizedLevel === 'CRITICAL' ? 'HIGH' : normalizedLevel;
          if (wardSeverityMap[ward][effectiveLevel] !== undefined) {
            wardSeverityMap[ward][effectiveLevel]++;
          }
        });

        Object.keys(wardSeverityMap).forEach(ward => {
          const counts = wardSeverityMap[ward];
          // Check in order: HIGH, MEDIUM, LOW
          const dominant = counts.HIGH > 0 ? 'HIGH' :
                          counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
          severityCounts[dominant] = (severityCounts[dominant] || 0) + 1;
        });
      }

      return {
        severityCounts,
        totalWards: Object.keys(wardSeverityMap).length
      };
    } catch (error) {
      console.error('Error fetching severity data:', error);
      throw error; // Re-throw to be caught by loadTrendData
    }
  }, [getIndicatorThreshold]);

  // Fetch groundwater severity data for a date (for pie charts)
  const fetchGroundwaterSeverityData = useCallback(async (date) => {
    const threshold = getIndicatorThreshold(groundwaterIndicatorName);
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const start = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
      const result = await fetchGroundwaterSeverityDataForDateRange(start, end, threshold, city);
      const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
      const severityCounts = { LOW: 0, MEDIUM: 0, HIGH: 0 };
      const wardSeverityMap = {};
      records.forEach((record) => {
        const key = record.ward_number ?? record.ward_name;
        if (key == null) return;
        const depletion = Number(record.depletion_level_meters);
        const severity = calculateGroundwaterSeverityLevel(isNaN(depletion) ? 0 : depletion, threshold);
        if (!wardSeverityMap[key]) wardSeverityMap[key] = { LOW: 0, MEDIUM: 0, HIGH: 0 };
        wardSeverityMap[key][severity]++;
      });
      Object.keys(wardSeverityMap).forEach((ward) => {
        const counts = wardSeverityMap[ward];
        const dominant = counts.HIGH > 0 ? 'HIGH' : counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW';
        severityCounts[dominant]++;
      });
      return { severityCounts, totalWards: Object.keys(wardSeverityMap).length };
    } catch (error) {
      console.error('Error fetching groundwater severity data:', error);
      throw error;
    }
  }, [getIndicatorThreshold, city]);

  // Fetch informal workers severity data for a date (for pie charts)
  const fetchInformalWorkersSeverityData = useCallback(async (date) => {
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const start = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

      const result = await fetchInformalWorkersSeverityDataForDateRange(start, end, city);
      const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
      const severityCounts = { LOW: 0, MEDIUM: 0, HIGH: 0 };
      const wardSeverityMap = {};
      
      records.forEach((record) => {
        const ward = record.ward_number;
        if (!ward) return;
        
        if (!wardSeverityMap[ward]) {
          wardSeverityMap[ward] = { LOW: 0, MEDIUM: 0, HIGH: 0 };
        }
        
        // Determine which severity field to use based on indicator name
        const severityPercentage = indicatorName === 'No show rate among informal workers' 
          ? record.no_show_rate_severity 
          : record.informal_income_loss_severity;
        
        if (severityPercentage !== null && severityPercentage !== undefined) {
          const severityLevel = calculateInformalWorkersSeverityLevel(severityPercentage);
          wardSeverityMap[ward][severityLevel] = (wardSeverityMap[ward][severityLevel] || 0) + 1;
        }
      });

      Object.keys(wardSeverityMap).forEach(ward => {
        const counts = wardSeverityMap[ward];
        const dominant = counts.HIGH > 0 ? 'HIGH' : (counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW');
        severityCounts[dominant]++;
      });

      return { severityCounts, totalWards: Object.keys(wardSeverityMap).length };
    } catch (error) {
      console.error('Error fetching informal workers severity data:', error);
      return { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
    }
  }, [indicatorName, city]);

  const fetchConstructionLaborSeverityData = useCallback(async (date) => {
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const start = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

      const result = await fetchConstructionLaborSeverityDataForDateRange(start, end, city);
      const records = result.ward_data || result.data || (Array.isArray(result) ? result : []);
      const severityCounts = { LOW: 0, MEDIUM: 0, HIGH: 0 };
      const wardSeverityMap = {};
      
      records.forEach((record) => {
        const ward = record.ward_number;
        if (!ward) return;
        
        if (!wardSeverityMap[ward]) {
          wardSeverityMap[ward] = { LOW: 0, MEDIUM: 0, HIGH: 0 };
        }
        
        // Determine which severity field and calculation function to use based on indicator name
        let severityPercentage = null;
        let severityLevel = 'LOW';
        
        if (indicatorName === 'Lost labor hours due to heat stress') {
          severityPercentage = record.lost_hour_severity_pct;
          if (severityPercentage !== null && severityPercentage !== undefined) {
            severityLevel = calculateLostLaborHoursSeverityLevel(severityPercentage);
          }
        } else if (indicatorName === '% of workers without access to shade/water') {
          severityPercentage = record.no_shade_water_severity_pct;
          if (severityPercentage !== null && severityPercentage !== undefined) {
            severityLevel = calculateNoShadeWaterSeverityLevel(severityPercentage);
          }
        } else if (indicatorName === 'Reported workplace heat illness cases') {
          severityPercentage = record.heat_illness_severity_pct;
          if (severityPercentage !== null && severityPercentage !== undefined) {
            severityLevel = calculateHeatIllnessSeverityLevel(severityPercentage);
          }
        }
        
        wardSeverityMap[ward][severityLevel] = (wardSeverityMap[ward][severityLevel] || 0) + 1;
      });

      Object.keys(wardSeverityMap).forEach(ward => {
        const counts = wardSeverityMap[ward];
        const dominant = counts.HIGH > 0 ? 'HIGH' : (counts.MEDIUM > 0 ? 'MEDIUM' : 'LOW');
        severityCounts[dominant]++;
      });

      return { severityCounts, totalWards: Object.keys(wardSeverityMap).length };
    } catch (error) {
      console.error('Error fetching construction labor severity data:', error);
      return { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
    }
  }, [indicatorName, city]);

  // Fetch transport disruptions severity data for a date (for pie charts).
  // Uses the same API and aggregation as the map (fetchTransportDisruptionsDataForWards) so pie colors and counts always match the map.
  const fetchTransportDisruptionsSeverityData = useCallback(async (date) => {
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const start = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;

      const threshold = getIndicatorThreshold(transportDisruptionsIndicatorName);
      const wardData = await fetchTransportDisruptionsDataForWards(start, end, threshold, city);
      const severityCounts = { LOW: 0, MEDIUM: 0, HIGH: 0 };

      Object.keys(wardData).forEach((wardNum) => {
        const dominant = (wardData[wardNum].dominant_severity || 'LOW').toUpperCase();
        const normalized = dominant === 'CRITICAL' ? 'HIGH' : dominant;
        if (severityCounts[normalized] !== undefined) {
          severityCounts[normalized]++;
        }
      });

      return { severityCounts, totalWards: Object.keys(wardData).length };
    } catch (error) {
      console.error('Error fetching transport disruptions severity data:', error);
      return { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
    }
  }, [city, getIndicatorThreshold]);

  // Generic: fetch severity counts from any DataForWards API (so pie matches map)
  const fetchSeverityFromDataForWards = useCallback(async (date, fetchFn, indicatorNameKey) => {
    const name = INDICATOR_NAMES[indicatorNameKey] || indicatorNameKey;
    try {
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const start = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      const end = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
      const threshold = getIndicatorThreshold(name);
      const wardData = await fetchFn(start, end, threshold, city);
      const severityCounts = { LOW: 0, MEDIUM: 0, HIGH: 0 };
      Object.keys(wardData).forEach((wardNum) => {
        const dominant = (wardData[wardNum].dominant_severity || 'LOW').toUpperCase();
        const normalized = dominant === 'CRITICAL' ? 'HIGH' : dominant;
        if (severityCounts[normalized] !== undefined) severityCounts[normalized]++;
      });
      return { severityCounts, totalWards: Object.keys(wardData).length };
    } catch (error) {
      console.error(`Error fetching severity data for ${name}:`, error);
      return { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
    }
  }, [city, getIndicatorThreshold]);

  // Convert dates to strings for reliable dependency tracking
  const startDateKey = startDate ? `${startDate.getFullYear()}-${startDate.getMonth()}-${startDate.getDate()}` : null;
  const endDateKey = endDate ? `${endDate.getFullYear()}-${endDate.getMonth()}-${endDate.getDate()}` : null;
  const selectedDateKey = selectedDate ? `${selectedDate.getFullYear()}-${selectedDate.getMonth()}-${selectedDate.getDate()}` : null;
  
  // Debug logging for date changes
  useEffect(() => {
    console.log('[TrendContainer] Date range changed:', {
      startDate: startDateKey,
      endDate: endDateKey,
      selectedDate: selectedDateKey,
      powerOutageMode
    });
  }, [startDateKey, endDateKey, selectedDateKey, powerOutageMode]);

  // Render power outage trend
  const renderPowerOutageTrend = useCallback(async () => {
    const wardNumber = selectedWard ? selectedWard.id : null;
    
    // Calculate date range from startDate and endDate props
    let dateRangeStart = '2025-05-01';
    let dateRangeEnd = '2025-05-31';
    
    if (startDate && endDate) {
      const startYear = startDate.getFullYear();
      const startMonth = startDate.getMonth() + 1;
      const startDay = startDate.getDate();
      dateRangeStart = `${startYear}-${String(startMonth).padStart(2, '0')}-${String(startDay).padStart(2, '0')}`;
      
      const endYear = endDate.getFullYear();
      const endMonth = endDate.getMonth() + 1;
      const endDay = endDate.getDate();
      dateRangeEnd = `${endYear}-${String(endMonth).padStart(2, '0')}-${String(endDay).padStart(2, '0')}`;
    } else if (selectedDate) {
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth() + 1;
      dateRangeStart = `${year}-${String(month).padStart(2, '0')}-01`;
      const lastDay = new Date(year, month, 0).getDate();
      dateRangeEnd = `${year}-${String(month).padStart(2, '0')}-${lastDay}`;
    }
    
    console.log('[TrendContainer] Fetching trend data:', {
      dateRangeStart,
      dateRangeEnd,
      wardNumber,
      powerOutageMode
    });
    
    // Get month and year for title from startDate/endDate (not from trendData)
    const dateForTitle = startDate || selectedDate || new Date(2025, 4, 1);
    const monthName = dateForTitle.toLocaleDateString('en-US', { month: 'long' });
    const year = dateForTitle.getFullYear();
    const month = dateForTitle.getMonth();
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();
    
    // Get threshold for power outage indicator
    const indicatorName = 'Power outages (duration and geographic scope)';
    const threshold = getIndicatorThreshold(indicatorName);
    const trendData = await fetchPowerOutageTrendData(dateRangeStart, dateRangeEnd, wardNumber, threshold, city);

    // Handle power outage data (may be null)
    let dates = [];
    let durations = [];
    let consumers = [];
    let dateLabels = [];
    let formattedDates = [];
    
    if (trendData) {
      dates = Object.keys(trendData).sort();
      durations = dates.map(d => trendData[d].total_duration);
      consumers = dates.map(d => trendData[d].total_consumers);
      
      // Format dates to show only day numbers for x-axis
      dateLabels = dates.map(dateStr => {
        const date = new Date(dateStr);
        return date.getDate();
      });
      
      // Format dates for tooltip display
      formattedDates = dates.map(dateStr => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
      });
    } else {
      // No power outage data - set error but continue to show temperature
      setError('Power outage trend data is not available');
    }

    // Generate date range for temperature data based on startDate and endDate
    const tempDates = [];
    let startDateForTemp = startDate || (selectedDate ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1) : new Date(2025, 4, 1));
    let endDateForTemp = endDate || (selectedDate ? new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0) : new Date(2025, 4, 31));
    
    // Normalize dates to midnight to avoid time component issues
    startDateForTemp = new Date(startDateForTemp.getFullYear(), startDateForTemp.getMonth(), startDateForTemp.getDate());
    endDateForTemp = new Date(endDateForTemp.getFullYear(), endDateForTemp.getMonth(), endDateForTemp.getDate());
    
    // Limit end date to today if it's in the future (temperature data only available up to current date)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (endDateForTemp > today) {
      endDateForTemp = today;
    }
    
    // Generate all dates between startDate and endDate (inclusive)
    const currentDate = new Date(startDateForTemp);
    const endDateTimestamp = endDateForTemp.getTime();
    
    while (currentDate.getTime() <= endDateTimestamp) {
      tempDates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const maxTemperatureData = {};
    const wardNumForTemp = wardNumber || 1;
    
    const tempPromises = tempDates.map(date => fetchWeatherForDate(wardNumForTemp, date, city));
    const tempResults = await Promise.all(tempPromises);
    tempResults.forEach((result, idx) => {
      if (result && !result.error) {
        if (result.max_temp_c !== null && result.max_temp_c !== undefined) {
          maxTemperatureData[tempDates[idx]] = result.max_temp_c;
        }
      }
    });

    let traces = [];
    let layout = {};

    // Calculate aligned ticks for both axes (normalized to 0-1, then mapped back)
    // Both axes start from 0
    const calculateAlignedTicks = (max1, max2, desiredTicks = 5, isIntegerAxis = false) => {
      // Ensure max values are at least 1 to avoid division issues
      const safeMax1 = Math.max(max1, 1);
      const safeMax2 = Math.max(max2, 1);
      
      // For integer-only axes (like consumers), use only integer ticks
      if (isIntegerAxis && safeMax2 <= 1) {
        return { ticks1: [0, safeMax1], ticks2: [0, 1] };
      }
      
      // For very small ranges, use fewer ticks to avoid duplicates
      const adjustedTicks = (safeMax2 <= 2 || safeMax1 <= 2) ? 3 : desiredTicks;
      
      // Normalize both ranges to 0-1 (first tick is always 0, last is 1)
      const normalizedTicks = [];
      for (let i = 0; i < adjustedTicks; i++) {
        normalizedTicks.push(i / (adjustedTicks - 1));
      }
      
      // Map normalized ticks back to actual values for each axis (starting from 0)
      let ticks1 = normalizedTicks.map(n => safeMax1 * n);
      let ticks2 = normalizedTicks.map(n => safeMax2 * n);
      
      // For integer-only axes, round to integers
      if (isIntegerAxis) {
        ticks2 = ticks2.map(t => Math.round(t));
      }
      
      // Remove duplicates and ensure unique values
      ticks1 = [...new Set(ticks1)].sort((a, b) => a - b);
      ticks2 = [...new Set(ticks2)].sort((a, b) => a - b);
      
      return { ticks1, ticks2 };
    };

    if (powerOutageMode === 'duration') {
      // Generate full month date range for temperature (all days 1 to lastDayOfMonth)
      const allDaysInMonth = [];
      const allDayLabels = [];
      const allFormattedDates = [];
      for (let day = 1; day <= lastDayOfMonth; day++) {
        const fullDate = new Date(year, month, day);
        allDaysInMonth.push(fullDate.toISOString().split('T')[0]);
        allDayLabels.push(day);
        allFormattedDates.push(fullDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
      }
      
      // Calculate temperature range (starting from 0) - use all available temperature data
      const allTempValues = allDaysInMonth.map(d => maxTemperatureData[d]).filter(v => v !== null && v !== undefined);
      const tempMax = allTempValues.length > 0 ? Math.ceil(Math.max(...allTempValues)) : 40;
      
      // Calculate duration range (starting from 0) - use 1 as default if no data
      const durationMax = durations.length > 0 ? Math.max(...durations) : 1;
      
      // Calculate aligned ticks (both start from 0)
      const { ticks1: tempTicks, ticks2: durationTicks } = calculateAlignedTicks(
        tempMax, durationMax, 5
      );

      // Only add power outage bar trace if there's actual data
      if (durations.length > 0 && dateLabels.length > 0) {
        traces.push({
          x: dateLabels,
          y: durations,
          type: 'bar',
          name: 'Unplanned Power Outage Duration',
          yaxis: 'y2',
          marker: { color: '#000000', opacity: 0.8 },
          customdata: formattedDates,
          hovertemplate: '<b>DATE:</b> %{customdata}<br><b>Duration of Unplanned Outage:</b> %{y:,.0f} minutes<extra></extra>',
          width: 0.6
        });
      }

      // Always show temperature data if available
      if (Object.keys(maxTemperatureData).length > 0) {
        // Map temperature data to all days in the month
        const maxTempValuesForTrace = allDaysInMonth.map(d => maxTemperatureData[d] || null);
        traces.push({
          x: allDayLabels,
          y: maxTempValuesForTrace,
          type: 'scatter',
          mode: 'lines+markers',
          name: 'Max Temperature',
          yaxis: 'y',
          line: { color: '#f97316', width: 3 },
          marker: { size: 7, color: '#f97316' },
          customdata: allFormattedDates,
          hovertemplate: '<b>DATE:</b> %{customdata}<br><b>Maximum temperature:</b> %{y:.1f}°C<extra></extra>'
        });
      }

      layout = {
        title: {
          text: wardNumber ? `Ward ${wardNumber} - Power Outage & Temperature Trend - ${monthName} ${year}` : `City-Level Unplanned Power Outage & Temperature Trend - ${monthName} ${year}`,
          font: { size: window.innerWidth <= 480 ? 11 : (window.innerWidth <= 768 ? 12 : 14) },
          y: 0.98,
          yref: 'container',
          yanchor: 'top',
          pad: { t: 10 }
        },
        xaxis: { 
          title: { text: '', standoff: 5 },
          showgrid: false,
          showticklabels: true,
          automargin: true,
          tickmode: 'linear',
          tick0: 1,
          dtick: 2,
          range: [0.5, lastDayOfMonth + 0.5]
        },
        yaxis: {
          title: { 
            text: 'Temperature (°C)', 
            standoff: 20,
            font: { color: '#f97316', size: 12 }
           },
          tickfont: { color: '#f97316', size: 10 },
          side: 'left',
          tickmode: 'array',
          tickvals: tempTicks.filter(t => t !== 0 && t !== -1),
          ticktext: tempTicks.filter(t => t !== 0 && t !== -1).map(t => Math.round(t)),
          showgrid: true,
          gridcolor: '#e5e7eb',
          zeroline: false,
          range: [0, tempMax],
          automargin: true
        },
        yaxis2: {
          title: { 
            text: 'Unplanned Power Outage Duration (minutes)', 
            standoff: 20,
            font: { color: '#000000', size: 12 }
           },
          tickfont: { color: '#000000', size: 10 },
          overlaying: 'y',
          side: 'right',
          tickmode: 'array',
          tickvals: durationTicks.filter(t => t !== 0 && t !== -1),
          ticktext: durationTicks.filter(t => t !== 0 && t !== -1).map(t => Math.round(t).toLocaleString()),
          showgrid: false,
          zeroline: false,
          range: [0, durationMax],
          automargin: true
        },
        margin: { 
          t: window.innerWidth <= 480 ? 50 : 70, 
          b: window.innerWidth <= 480 ? 40 : 50, 
          l: window.innerWidth <= 480 ? 40 : 60, 
          r: window.innerWidth <= 480 ? 40 : 60, 
          pad: 5 
        },
        height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 350 : 400),
        showlegend: true,
        legend: { x: 0.5, y: -0.18, xanchor: 'center', orientation: 'h', bgcolor: 'rgba(255,255,255,0.8)' },
        plot_bgcolor: 'white',
        paper_bgcolor: 'white',
        autosize: true,
        hovermode: 'closest',
        hoverdistance: 5,
        hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 } }
      };
    } else if (powerOutageMode === 'consumers') {
      // Generate full month date range for temperature (all days 1 to lastDayOfMonth)
      const allDaysInMonth = [];
      const allDayLabels = [];
      const allFormattedDates = [];
      for (let day = 1; day <= lastDayOfMonth; day++) {
        const fullDate = new Date(year, month, day);
        allDaysInMonth.push(fullDate.toISOString().split('T')[0]);
        allDayLabels.push(day);
        allFormattedDates.push(fullDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
      }
      
      // Calculate temperature range (starting from 0) - use all available temperature data
      const allTempValues = allDaysInMonth.map(d => maxTemperatureData[d]).filter(v => v !== null && v !== undefined);
      const tempMax = allTempValues.length > 0 ? Math.ceil(Math.max(...allTempValues)) : 40;
      
      // Calculate consumers range (starting from 0) - use 1 as default if no data
      const consumersMax = consumers.length > 0 ? Math.max(...consumers) : 1;
      
      // Calculate aligned ticks (both start from 0)
      // Consumers axis is integer-only
      const { ticks1: tempTicks, ticks2: consumersTicks } = calculateAlignedTicks(
        tempMax, consumersMax, 5, true
      );

      // Only add power outage bar trace if there's actual data
      if (consumers.length > 0 && dateLabels.length > 0) {
        traces.push({
          x: dateLabels,
          y: consumers,
          type: 'bar',
          name: 'Consumers Affected',
          yaxis: 'y2',
          marker: { color: '#2563eb', opacity: 0.8 },
          customdata: formattedDates,
          hovertemplate: '<b>DATE:</b> %{customdata}<br><b>Total Consumers Affected:</b> %{y:,.0f}<extra></extra>',
          width: 0.6
        });
      }

      // Always show temperature data if available
      if (Object.keys(maxTemperatureData).length > 0) {
        // Map temperature data to all days in the month
        const maxTempValuesForTrace = allDaysInMonth.map(d => maxTemperatureData[d] || null);
        traces.push({
          x: allDayLabels,
          y: maxTempValuesForTrace,
          type: 'scatter',
          mode: 'lines+markers',
          name: 'Max Temperature',
          yaxis: 'y',
          line: { color: '#f97316', width: 3 },
          marker: { size: 7, color: '#f97316' },
          customdata: allFormattedDates,
          hovertemplate: '<b>DATE:</b> %{customdata}<br><b>Maximum temperature:</b> %{y:.1f}°C<extra></extra>'
        });
      }

      layout = {
        title: {
          text: wardNumber ? `Ward ${wardNumber} - Consumers Affected & Temperature Trend - ${monthName} ${year}` : `City-Level Consumers Affected & Temperature Trend - ${monthName} ${year}`,
          font: { size: window.innerWidth <= 480 ? 11 : (window.innerWidth <= 768 ? 12 : 14) },
          y: 0.98,
          yref: 'container',
          yanchor: 'top',
          pad: { t: 10 }
        },
        xaxis: { 
          title: { text: '', standoff: 5 },
          showgrid: false,
          showticklabels: true,
          automargin: true,
          tickmode: 'linear',
          tick0: 1,
          dtick: 2,
          range: [0.5, lastDayOfMonth + 0.5]
        },
        yaxis: {
          title: { 
            text: 'Temperature (°C)', 
            standoff: 20,
            font: { color: '#f97316', size: 12 }
           },
          tickfont: { color: '#f97316', size: 10 },
          side: 'left',
          tickmode: 'array',
          tickvals: tempTicks.filter(t => t !== 0 && t !== -1),
          ticktext: tempTicks.filter(t => t !== 0 && t !== -1).map(t => Math.round(t)),
          showgrid: true,
          gridcolor: '#e5e7eb',
          zeroline: false,
          range: [0, tempMax],
          automargin: true
        },
        yaxis2: {
          title: { 
            text: 'Consumers Affected', 
            standoff: 20,
            font: { color: '#2563eb', size: 12 }
          },
          tickfont: { color: '#2563eb', size: 10 },
          overlaying: 'y',
          side: 'right',
          tickmode: 'array',
          tickvals: consumersTicks.filter(t => t !== 0 && t !== -1),
          ticktext: consumersTicks.filter(t => t !== 0 && t !== -1).map(t => Math.round(t).toLocaleString()),
          showgrid: false,
          zeroline: false,
          range: [0, consumersMax],
          automargin: true
        },
        margin: { 
          t: window.innerWidth <= 480 ? 50 : 70, 
          b: window.innerWidth <= 480 ? 40 : 50, 
          l: window.innerWidth <= 480 ? 40 : 60, 
          r: window.innerWidth <= 480 ? 40 : 60, 
          pad: 5 
        },
        height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 350 : 400),
        showlegend: true,
        legend: { x: 0.5, y: -0.18, xanchor: 'center', orientation: 'h', bgcolor: 'rgba(255,255,255,0.8)' },
        plot_bgcolor: 'white',
        paper_bgcolor: 'white',
        autosize: true,
        hovermode: 'closest',
        hoverdistance: 5,
        hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 } }
      };
    }

    setPlotData(traces);
    setPlotLayout(layout);
  }, [startDateKey, endDateKey, selectedDateKey, selectedWard, powerOutageMode, getIndicatorThreshold]);

  // Render groundwater trend (GWL or Depletion % over time)
  const renderGroundwaterTrend = useCallback(async () => {
    let dateRangeStart = '2020-01-01';
    let dateRangeEnd = '2025-12-31';
    if (startDate && endDate) {
      dateRangeStart = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      dateRangeEnd = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    } else if (selectedDate) {
      const y = selectedDate.getFullYear();
      const m = selectedDate.getMonth() + 1;
      dateRangeStart = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      dateRangeEnd = `${y}-${String(m).padStart(2, '0')}-${lastDay}`;
    }
    // Groundwater API responses are ward_name based (ward_number may not be present),
    // so for ward-level trend we prefer the clicked ward name.
    const wardKey = selectedWard
      ? (selectedWard.data?.ward_name ?? selectedWard.name ?? null)
      : null;
    const mode = groundwaterMode === 'gwl' ? 'gwl' : 'depletion_pct';
    let trendData;
    try {
      trendData = await fetchGroundwaterTrendData(dateRangeStart, dateRangeEnd, wardKey, mode, city);
    } catch (e) {
      setError(`Groundwater trend: ${e.message}`);
      setPlotData([]);
      setPlotLayout({});
      return;
    }
    if (!trendData || !trendData.dates || trendData.dates.length === 0) {
      setError('Groundwater trend data is not available');
      setPlotData([]);
      setPlotLayout({});
      return;
    }
    const dates = trendData.dates;
    const values = mode === 'gwl' ? trendData.gwl : trendData.severity_index;
    const valueByDate = {};
    dates.forEach((d, idx) => {
      valueByDate[d] = values[idx];
    });

    const dateForTitle = startDate || selectedDate || new Date();
    const monthName = dateForTitle.toLocaleDateString('en-US', { month: 'long' });
    const year = dateForTitle.getFullYear();
    const month = dateForTitle.getMonth();
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

    // Build full month axis (1..last day), like Power Outage trend charts
    const allDaysInMonth = [];
    const allDayLabels = [];
    const allFormattedDates = [];
    for (let day = 1; day <= lastDayOfMonth; day++) {
      const fullDate = new Date(year, month, day);
      const iso = fullDate.toISOString().split('T')[0];
      allDaysInMonth.push(iso);
      allDayLabels.push(day);
      allFormattedDates.push(fullDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
    }

    // Fetch temperature series (max temp) for the same period
    const selectedWardNum =
      (selectedWard && selectedWard.numericWardNum != null ? selectedWard.numericWardNum : null) ??
      (selectedWard && selectedWard.id != null ? parseInt(selectedWard.id, 10) : null) ??
      1;
    const wardNumForTemp = Number.isFinite(selectedWardNum) ? selectedWardNum : 1;

    const maxTemperatureData = {};
    const tempPromises = allDaysInMonth.map((d) => fetchWeatherForDate(wardNumForTemp, d, city));
    const tempResults = await Promise.all(tempPromises);
    tempResults.forEach((result, idx) => {
      if (result && !result.error && result.max_temp_c !== null && result.max_temp_c !== undefined) {
        maxTemperatureData[allDaysInMonth[idx]] = result.max_temp_c;
      }
    });

    const y1Label = 'Temperature (°C)';
    const y2Label = mode === 'gwl' ? 'Current GWL (m)' : 'Depletion %';
    const gwlColor = mode === 'gwl' ? '#0ea5e9' : '#7c3aed';
    const tempColor = '#f97316';

    const groundwaterValuesForTrace = allDaysInMonth.map((d) => {
      const v = valueByDate[d];
      return v === null || v === undefined ? null : Number(v);
    });
    const tempValuesForTrace = allDaysInMonth.map((d) => (maxTemperatureData[d] ?? null));

    const traces = [];

    const hasGroundwaterData = groundwaterValuesForTrace.some((v) => v !== null && v !== undefined && !Number.isNaN(v));
    if (hasGroundwaterData) {
      traces.push({
        x: allDayLabels,
        y: groundwaterValuesForTrace,
        type: 'bar',
        name: mode === 'gwl' ? 'Current GWL' : 'Depletion %',
        yaxis: 'y2',
        marker: { color: gwlColor, opacity: 0.85 },
        customdata: allFormattedDates,
        hovertemplate:
          '<b>DATE:</b> %{customdata}<br><b>' +
          y2Label +
          ':</b> %{y:.2f}' +
          (mode === 'depletion_pct' ? '%' : ' m') +
          '<extra></extra>',
        width: 0.6
      });
    } else {
      setError('Groundwater trend data is not available');
    }

    const hasTempData = tempValuesForTrace.some((v) => v !== null && v !== undefined && !Number.isNaN(v));
    if (hasTempData) {
      traces.push({
        x: allDayLabels,
        y: tempValuesForTrace,
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Max Temperature',
        yaxis: 'y',
        line: { color: tempColor, width: 3 },
        marker: { size: 7, color: tempColor },
        customdata: allFormattedDates,
        hovertemplate: '<b>DATE:</b> %{customdata}<br><b>Maximum temperature:</b> %{y:.1f}°C<extra></extra>'
      });
    }

    const allTempValues = tempValuesForTrace.filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
    const tempMax = allTempValues.length > 0 ? Math.ceil(Math.max(...allTempValues)) : 40;
    const allGwValues = groundwaterValuesForTrace.filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
    const gwMax = mode === 'depletion_pct'
      ? Math.max(100, allGwValues.length ? Math.max(...allGwValues) : 0)
      : (allGwValues.length ? Math.max(...allGwValues) : 1);

    const calculateAlignedTicks = (max1, max2, desiredTicks = 5, isIntegerAxis = false) => {
      const safeMax1 = Math.max(max1, 1);
      const safeMax2 = Math.max(max2, 1);
      const adjustedTicks = (safeMax2 <= 2 || safeMax1 <= 2) ? 3 : desiredTicks;
      const normalizedTicks = [];
      for (let i = 0; i < adjustedTicks; i++) normalizedTicks.push(i / (adjustedTicks - 1));
      let ticks1 = normalizedTicks.map(n => safeMax1 * n);
      let ticks2 = normalizedTicks.map(n => safeMax2 * n);
      if (isIntegerAxis) ticks2 = ticks2.map(t => Math.round(t));
      ticks1 = [...new Set(ticks1)].sort((a, b) => a - b);
      ticks2 = [...new Set(ticks2)].sort((a, b) => a - b);
      return { ticks1, ticks2 };
    };
    const { ticks1: tempTicks, ticks2: gwTicks } = calculateAlignedTicks(tempMax, gwMax, 5, false);

    const gwTabName = mode === 'gwl' ? 'Current Groundwater Level' : 'Depletion %';
    const title = wardKey
      ? `Ward ${wardNumForTemp} - ${gwTabName} - ${monthName} ${year}`
      : `City-Level ${gwTabName} - ${monthName} ${year}`;

    setPlotData(traces);
    setPlotLayout({
      title: {
        text: title,
        font: { size: window.innerWidth <= 480 ? 11 : (window.innerWidth <= 768 ? 12 : 14) },
        y: 0.98,
        yref: 'container',
        yanchor: 'top',
        pad: { t: 10 }
      },
      xaxis: {
        title: { text: '', standoff: 5 },
        showgrid: false,
        showticklabels: true,
        automargin: true,
        tickmode: 'linear',
        tick0: 1,
        dtick: 2,
        range: [0.5, lastDayOfMonth + 0.5]
      },
      yaxis: {
        title: { text: y1Label, standoff: 20, font: { color: tempColor, size: 12 } },
        tickfont: { color: tempColor, size: 10 },
        side: 'left',
        tickmode: 'array',
        tickvals: tempTicks.filter(t => t !== 0 && t !== -1),
        ticktext: tempTicks.filter(t => t !== 0 && t !== -1).map(t => Math.round(t)),
        showgrid: true,
        gridcolor: '#e5e7eb',
        zeroline: false,
        range: [0, tempMax],
        automargin: true
      },
      yaxis2: {
        title: { text: y2Label, standoff: 20, font: { color: gwlColor, size: 12 } },
        tickfont: { color: gwlColor, size: 10 },
        overlaying: 'y',
        side: 'right',
        tickmode: 'array',
        tickvals: gwTicks.filter(t => t !== 0 && t !== -1),
        ticktext: gwTicks.filter(t => t !== 0 && t !== -1).map(t => (mode === 'depletion_pct' ? Math.round(t) : Number(t).toFixed(1))),
        showgrid: false,
        zeroline: false,
        range: [0, gwMax],
        automargin: true
      },
      margin: {
        t: window.innerWidth <= 480 ? 50 : 70,
        b: window.innerWidth <= 480 ? 40 : 50,
        l: window.innerWidth <= 480 ? 40 : 60,
        r: window.innerWidth <= 480 ? 40 : 60,
        pad: 5
      },
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 350 : 400),
      showlegend: true,
      legend: { x: 0.5, y: -0.18, xanchor: 'center', orientation: 'h', bgcolor: 'rgba(255,255,255,0.8)' },
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      autosize: true,
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 } }
    });
  }, [startDateKey, endDateKey, selectedDateKey, selectedWard, groundwaterMode]);

  // Render informal workers trend
  const renderInformalWorkersTrend = useCallback(async () => {
    let dateRangeStart = '2026-01-01';
    let dateRangeEnd = '2026-02-04';
    if (startDate && endDate) {
      dateRangeStart = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      dateRangeEnd = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    } else if (selectedDate) {
      const y = selectedDate.getFullYear();
      const m = selectedDate.getMonth() + 1;
      dateRangeStart = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      dateRangeEnd = `${y}-${String(m).padStart(2, '0')}-${lastDay}`;
    }

    const wardNumber = selectedWard ? (selectedWard.numericWardNum ?? selectedWard.id) : null;
    const indicatorType = indicatorName === 'No show rate among informal workers' ? 'no_show_rate' : 'daily_wage_loss';
    
    // Always fetch temperature data first, regardless of informal workers data availability
    const dateForTitle = startDate || selectedDate || new Date();
    const monthName = dateForTitle.toLocaleDateString('en-US', { month: 'long' });
    const year = dateForTitle.getFullYear();
    const month = dateForTitle.getMonth();
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

    const allDaysInMonth = [];
    const allDayLabels = [];
    const allFormattedDates = [];
    for (let day = 1; day <= lastDayOfMonth; day++) {
      const fullDate = new Date(year, month, day);
      const iso = fullDate.toISOString().split('T')[0];
      allDaysInMonth.push(iso);
      allDayLabels.push(day);
      allFormattedDates.push(fullDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
    }

    const selectedWardNum = selectedWard?.numericWardNum ?? selectedWard?.id ?? 1;
    const wardNumForTemp = Number.isFinite(selectedWardNum) ? selectedWardNum : 1;

    // Fetch temperature data regardless of informal workers data availability
    const maxTemperatureData = {};
    const tempPromises = allDaysInMonth.map((d) => fetchWeatherForDate(wardNumForTemp, d, city));
    const tempResults = await Promise.all(tempPromises);
    tempResults.forEach((result, idx) => {
      if (result && !result.error && result.max_temp_c !== null && result.max_temp_c !== undefined) {
        maxTemperatureData[allDaysInMonth[idx]] = result.max_temp_c;
      }
    });

    // Try to fetch informal workers data, but don't fail if unavailable
    let trendData;
    let hasInformalWorkersData = false;
    const valueByDate = {}; // Initialize empty object - will be populated if data is available
    
    try {
      trendData = await fetchInformalWorkersTrendData(dateRangeStart, dateRangeEnd, wardNumber, indicatorType, city);
      if (trendData && trendData.dates && trendData.dates.length > 0) {
        hasInformalWorkersData = true;
        
        // Build value mapping if we have informal workers data
        let values = [];
        if (informalWorkersMode === 'severity') {
          values = indicatorType === 'no_show_rate' 
            ? trendData.no_show_rate_severity 
            : trendData.informal_income_loss_severity;
        } else if (informalWorkersMode === 'total_registered') {
          values = trendData.total_registered_e_shram;
        } else {
          values = trendData.total_heat_illness;
        }
        trendData.dates.forEach((d, idx) => {
          valueByDate[d] = values[idx];
        });
      }
    } catch (e) {
      // Log error but continue to show temperature data
      console.warn('Informal workers trend data unavailable:', e.message);
      trendData = null;
    }

    // Set error message if informal workers data is missing, but don't prevent chart rendering
    if (!hasInformalWorkersData) {
      setError('Informal workers trend data is not available');
    } else {
      setError(null);
    }

    // Determine y2Label based on mode
    let y2Label = '';
    if (informalWorkersMode === 'severity') {
      y2Label = indicatorType === 'no_show_rate' ? 'No Show Rate (%)' : 'Daily Wage Loss (%)';
    } else if (informalWorkersMode === 'total_registered') {
      y2Label = 'Total Registered Workers';
    } else {
      y2Label = 'Total Heat Illness';
    }

    const y1Label = 'Temperature (°C)';
    const dataColor = informalWorkersMode === 'severity' ? '#7c3aed' : (informalWorkersMode === 'total_registered' ? '#0ea5e9' : '#ef4444');
    const tempColor = '#f97316';

    const dataValuesForTrace = allDaysInMonth.map((d) => {
      const v = valueByDate[d];
      return v === null || v === undefined ? null : Number(v);
    });
    const tempValuesForTrace = allDaysInMonth.map((d) => (maxTemperatureData[d] ?? null));

    const traces = [];

    // Always add temperature trace if available (even if informal workers data is missing)
    const hasTempData = tempValuesForTrace.some((v) => v !== null && v !== undefined && !Number.isNaN(v));
    if (hasTempData) {
      traces.push({
        x: allDayLabels,
        y: tempValuesForTrace,
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Max Temperature',
        yaxis: 'y',
        line: { color: tempColor, width: 3 },
        marker: { size: 7, color: tempColor },
        customdata: allFormattedDates,
        hovertemplate: '<b>DATE:</b> %{customdata}<br><b>Maximum temperature:</b> %{y:.1f}°C<extra></extra>'
      });
    }

    // Add informal workers data trace if available
    const hasData = dataValuesForTrace.some((v) => v !== null && v !== undefined && !Number.isNaN(v));
    if (hasData) {
      traces.push({
        x: allDayLabels,
        y: dataValuesForTrace,
        type: 'bar',
        name: y2Label,
        yaxis: 'y2',
        marker: { color: dataColor, opacity: 0.85 },
        customdata: allFormattedDates,
        hovertemplate: `<b>DATE:</b> %{customdata}<br><b>${y2Label}:</b> %{y:.${informalWorkersMode === 'severity' ? '1f' : '0f'}}${informalWorkersMode === 'severity' ? '%' : ''}<extra></extra>`,
        width: 0.6
      });
    }

    const allTempValues = tempValuesForTrace.filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
    const tempMax = allTempValues.length > 0 ? Math.ceil(Math.max(...allTempValues)) : 40;
    const allDataValues = dataValuesForTrace.filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
    // If no informal workers data, set a default range for y2 axis (it won't be shown but prevents errors)
    const dataMax = allDataValues.length > 0 ? Math.max(...allDataValues) * 1.1 : 1;

    const calculateAlignedTicks = (max1, max2, desiredTicks = 5, isIntegerAxis = false) => {
      const safeMax1 = Math.max(max1, 1);
      const safeMax2 = Math.max(max2, 1);
      const adjustedTicks = (safeMax2 <= 2 || safeMax1 <= 2) ? 3 : desiredTicks;
      const normalizedTicks = [];
      for (let i = 0; i < adjustedTicks; i++) normalizedTicks.push(i / (adjustedTicks - 1));
      let ticks1 = normalizedTicks.map(n => safeMax1 * n);
      let ticks2 = normalizedTicks.map(n => safeMax2 * n);
      if (isIntegerAxis) ticks2 = ticks2.map(t => Math.round(t));
      ticks1 = [...new Set(ticks1)].sort((a, b) => a - b);
      ticks2 = [...new Set(ticks2)].sort((a, b) => a - b);
      return { ticks1, ticks2 };
    };
    const { ticks1: tempTicks, ticks2: dataTicks } = calculateAlignedTicks(tempMax, dataMax, 5, informalWorkersMode !== 'severity');

    // Adjust title based on data availability (format: City-Level tab name - Month Year)
    const title = hasInformalWorkersData
      ? (wardNumber
          ? `Ward ${wardNumForTemp} - ${y2Label} - ${monthName} ${year}`
          : `City-Level ${y2Label} - ${monthName} ${year}`)
      : (wardNumber
          ? `Ward ${wardNumForTemp} - Temperature Trend - ${monthName} ${year}`
          : `City-Level Temperature Trend - ${monthName} ${year}`);

    setPlotData(traces);
    setPlotLayout({
      title: {
        text: title,
        font: { size: window.innerWidth <= 480 ? 11 : (window.innerWidth <= 768 ? 12 : 14) },
        y: 0.98,
        yref: 'container',
        yanchor: 'top',
        pad: { t: 10 }
      },
      xaxis: {
        title: { text: '', standoff: 5 },
        showgrid: false,
        showticklabels: true,
        automargin: true,
        tickmode: 'linear',
        tick0: 1,
        dtick: 2,
        range: [0.5, lastDayOfMonth + 0.5]
      },
      yaxis: {
        title: { text: y1Label, standoff: 20, font: { color: tempColor, size: 12 } },
        tickfont: { color: tempColor, size: 10 },
        side: 'left',
        tickmode: 'array',
        tickvals: tempTicks.filter(t => t !== 0 && t !== -1),
        ticktext: tempTicks.filter(t => t !== 0 && t !== -1).map(t => Math.round(t)),
        showgrid: true,
        gridcolor: '#e5e7eb',
        zeroline: false,
        range: [0, tempMax],
        automargin: true
      },
      yaxis2: {
        title: { text: y2Label, standoff: 20, font: { color: dataColor, size: 12 } },
        tickfont: { color: dataColor, size: 10 },
        overlaying: 'y',
        side: 'right',
        tickmode: 'array',
        tickvals: dataTicks.filter(t => t !== 0 && t !== -1),
        ticktext: dataTicks.filter(t => t !== 0 && t !== -1).map(t => (informalWorkersMode === 'severity' ? Number(t).toFixed(1) : Math.round(t))),
        showgrid: false,
        zeroline: false,
        range: [0, dataMax],
        automargin: true,
        visible: hasData // Only show y2 axis if we have informal workers data
      },
      margin: {
        t: window.innerWidth <= 480 ? 50 : 70,
        b: window.innerWidth <= 480 ? 40 : 50,
        l: window.innerWidth <= 480 ? 40 : 60,
        r: window.innerWidth <= 480 ? 40 : 60,
        pad: 5
      },
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 350 : 400),
      showlegend: true,
      legend: { x: 0.5, y: -0.18, xanchor: 'center', orientation: 'h', bgcolor: 'rgba(255,255,255,0.8)' },
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      autosize: true,
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 } }
    });
  }, [startDateKey, endDateKey, selectedDateKey, selectedWard, informalWorkersMode, indicatorName, city]);

  const renderConstructionLaborTrend = useCallback(async () => {
    let dateRangeStart = '2025-05-01';
    let dateRangeEnd = '2026-02-04';
    
    if (startDate && endDate) {
      dateRangeStart = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      dateRangeEnd = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    } else if (selectedDate) {
      const y = selectedDate.getFullYear();
      const m = selectedDate.getMonth() + 1;
      dateRangeStart = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      dateRangeEnd = `${y}-${String(m).padStart(2, '0')}-${lastDay}`;
    }

    const wardNumber = selectedWard ? (selectedWard.numericWardNum ?? selectedWard.id) : null;
    const indicatorType = indicatorName === 'Lost labor hours due to heat stress'
      ? 'lost_hours'
      : indicatorName === '% of workers without access to shade/water'
      ? 'no_shade_water'
      : 'heat_illness';
    
    // Always fetch temperature data first
    const dateForTitle = startDate || selectedDate || new Date();
    const monthName = dateForTitle.toLocaleDateString('en-US', { month: 'long' });
    const year = dateForTitle.getFullYear();
    const month = dateForTitle.getMonth();
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

    const allDaysInMonth = [];
    const allDayLabels = [];
    const allFormattedDates = [];
    for (let day = 1; day <= lastDayOfMonth; day++) {
      const fullDate = new Date(year, month, day);
      const iso = fullDate.toISOString().split('T')[0];
      allDaysInMonth.push(iso);
      allDayLabels.push(day);
      allFormattedDates.push(fullDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
    }

    const selectedWardNum = selectedWard?.numericWardNum ?? selectedWard?.id ?? 1;
    const wardNumForTemp = Number.isFinite(selectedWardNum) ? selectedWardNum : 1;

    // Fetch temperature data regardless of construction labor data availability
    const maxTemperatureData = {};
    const tempPromises = allDaysInMonth.map((d) => fetchWeatherForDate(wardNumForTemp, d, city));
    const tempResults = await Promise.all(tempPromises);
    tempResults.forEach((result, idx) => {
      if (result && !result.error && result.max_temp_c !== null && result.max_temp_c !== undefined) {
        maxTemperatureData[allDaysInMonth[idx]] = result.max_temp_c;
      }
    });

    // Try to fetch construction labor data, but don't fail if unavailable
    let trendData;
    let hasConstructionLaborData = false;
    const valueByDate = {};
    
    try {
      trendData = await fetchConstructionLaborTrendData(dateRangeStart, dateRangeEnd, wardNumber, indicatorType, city);
      if (trendData && trendData.dates && trendData.dates.length > 0) {
        hasConstructionLaborData = true;
        
        // Build value mapping if we have construction labor data
        let values = [];
        if (constructionLaborMode === 'severity') {
          if (indicatorType === 'lost_hours') {
            values = trendData.lost_hour_severity_pct || [];
          } else if (indicatorType === 'no_shade_water') {
            values = trendData.no_shade_water_severity_pct || [];
          } else {
            values = trendData.heat_illness_severity_pct || [];
          }
        } else {
          values = trendData.total_workers_present || [];
        }
        trendData.dates.forEach((d, idx) => {
          valueByDate[d] = values[idx];
        });
      }
    } catch (e) {
      console.warn('Construction labor trend data unavailable:', e.message);
      trendData = null;
    }

    // Set error message if construction labor data is missing, but don't prevent chart rendering
    if (!hasConstructionLaborData) {
      setError('Construction labor trend data is not available');
    } else {
      setError(null);
    }

    // Determine y2Label based on mode
    let y2Label = '';
    if (constructionLaborMode === 'severity') {
      if (indicatorType === 'lost_hours') {
        y2Label = 'Lost Labor Hours (%)';
      } else if (indicatorType === 'no_shade_water') {
        y2Label = '% Without Shade/Water';
      } else {
        y2Label = 'Heat Illness Cases (%)';
      }
    } else {
      y2Label = 'Total Present Workers';
    }

    const y1Label = 'Temperature (°C)';
    const dataColor = constructionLaborMode === 'severity' ? '#7c3aed' : '#0ea5e9';
    const tempColor = '#f97316';

    const dataValuesForTrace = allDaysInMonth.map((d) => {
      const v = valueByDate[d];
      return v === null || v === undefined ? null : Number(v);
    });
    const tempValuesForTrace = allDaysInMonth.map((d) => (maxTemperatureData[d] ?? null));

    const traces = [];

    // Always add temperature trace if available
    const hasTempData = tempValuesForTrace.some((v) => v !== null && v !== undefined && !Number.isNaN(v));
    if (hasTempData) {
      traces.push({
        x: allDayLabels,
        y: tempValuesForTrace,
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Max Temperature',
        yaxis: 'y',
        line: { color: tempColor, width: 3 },
        marker: { size: 7, color: tempColor },
        customdata: allFormattedDates,
        hovertemplate: '<b>DATE:</b> %{customdata}<br><b>Maximum temperature:</b> %{y:.1f}°C<extra></extra>'
      });
    }

    // Add construction labor data trace if available
    const hasData = dataValuesForTrace.some((v) => v !== null && v !== undefined && !Number.isNaN(v));
    if (hasData) {
      traces.push({
        x: allDayLabels,
        y: dataValuesForTrace,
        type: 'bar',
        name: y2Label,
        yaxis: 'y2',
        marker: { color: dataColor, opacity: 0.85 },
        customdata: allFormattedDates,
        hovertemplate: `<b>DATE:</b> %{customdata}<br><b>${y2Label}:</b> %{y:.${constructionLaborMode === 'severity' ? '1f' : '0f'}}${constructionLaborMode === 'severity' ? '%' : ''}<extra></extra>`,
        width: 0.6
      });
    }

    const allTempValues = tempValuesForTrace.filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
    const tempMax = allTempValues.length > 0 ? Math.ceil(Math.max(...allTempValues)) : 40;
    const allDataValues = dataValuesForTrace.filter((v) => v !== null && v !== undefined && !Number.isNaN(v));
    const dataMax = allDataValues.length > 0 ? Math.max(...allDataValues) * 1.1 : 1;

    const calculateAlignedTicks = (max1, max2, desiredTicks = 5, isIntegerAxis = false) => {
      const safeMax1 = Math.max(max1, 1);
      const safeMax2 = Math.max(max2, 1);
      const adjustedTicks = (safeMax2 <= 2 || safeMax1 <= 2) ? 3 : desiredTicks;
      const normalizedTicks = [];
      for (let i = 0; i < adjustedTicks; i++) normalizedTicks.push(i / (adjustedTicks - 1));
      let ticks1 = normalizedTicks.map(n => safeMax1 * n);
      let ticks2 = normalizedTicks.map(n => safeMax2 * n);
      if (isIntegerAxis) ticks2 = ticks2.map(t => Math.round(t));
      ticks1 = [...new Set(ticks1)].sort((a, b) => a - b);
      ticks2 = [...new Set(ticks2)].sort((a, b) => a - b);
      return { ticks1, ticks2 };
    };
    const { ticks1: tempTicks, ticks2: dataTicks } = calculateAlignedTicks(tempMax, dataMax, 5, constructionLaborMode !== 'severity');

    // Adjust title based on data availability (format: City-Level tab name - Month Year)
    const title = hasConstructionLaborData
      ? (wardNumber
          ? `Ward ${wardNumForTemp} - ${y2Label} - ${monthName} ${year}`
          : `City-Level ${y2Label} - ${monthName} ${year}`)
      : (wardNumber
          ? `Ward ${wardNumForTemp} - Temperature Trend - ${monthName} ${year}`
          : `City-Level Temperature Trend - ${monthName} ${year}`);

    setPlotData(traces);
    setPlotLayout({
      title: {
        text: title,
        font: { size: window.innerWidth <= 480 ? 11 : (window.innerWidth <= 768 ? 12 : 14) },
        y: 0.98,
        yref: 'container',
        yanchor: 'top',
        pad: { t: 10 }
      },
      xaxis: {
        title: { text: '', standoff: 5 },
        showgrid: false,
        showticklabels: true,
        automargin: true,
        tickmode: 'linear',
        tick0: 1,
        dtick: 2,
        range: [0.5, lastDayOfMonth + 0.5]
      },
      yaxis: {
        title: { text: y1Label, standoff: 20, font: { color: tempColor, size: 12 } },
        tickfont: { color: tempColor, size: 10 },
        side: 'left',
        tickmode: 'array',
        tickvals: tempTicks.filter(t => t !== 0 && t !== -1),
        ticktext: tempTicks.filter(t => t !== 0 && t !== -1).map(t => Math.round(t)),
        showgrid: true,
        gridcolor: '#e5e7eb',
        zeroline: false,
        range: [0, tempMax],
        automargin: true
      },
      yaxis2: {
        title: { text: y2Label, standoff: 20, font: { color: dataColor, size: 12 } },
        tickfont: { color: dataColor, size: 10 },
        overlaying: 'y',
        side: 'right',
        tickmode: 'array',
        tickvals: dataTicks.filter(t => t !== 0 && t !== -1),
        ticktext: dataTicks.filter(t => t !== 0 && t !== -1).map(t => (constructionLaborMode === 'severity' ? Number(t).toFixed(1) : Math.round(t))),
        showgrid: false,
        zeroline: false,
        range: [0, dataMax],
        automargin: true,
        visible: hasData
      },
      margin: {
        t: window.innerWidth <= 480 ? 50 : 70,
        b: window.innerWidth <= 480 ? 40 : 50,
        l: window.innerWidth <= 480 ? 40 : 60,
        r: window.innerWidth <= 480 ? 40 : 60,
        pad: 5
      },
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 350 : 400),
      showlegend: true,
      legend: { x: 0.5, y: -0.18, xanchor: 'center', orientation: 'h', bgcolor: 'rgba(255,255,255,0.8)' },
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      autosize: true,
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 } }
    });
  }, [startDateKey, endDateKey, selectedDateKey, selectedWard, constructionLaborMode, indicatorName, city]);

  // Render transport disruptions trend (bar chart + temperature)
  const renderTransportDisruptionsTrend = useCallback(async () => {
    const wardNumber = selectedWard ? selectedWard.id : null;
    let dateRangeStart = '2025-05-01';
    let dateRangeEnd = '2025-05-31';
    if (startDate && endDate) {
      dateRangeStart = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      dateRangeEnd = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    } else if (selectedDate) {
      const y = selectedDate.getFullYear();
      const m = selectedDate.getMonth() + 1;
      dateRangeStart = `${y}-${String(m).padStart(2, '0')}-01`;
      const lastDay = new Date(y, m, 0).getDate();
      dateRangeEnd = `${y}-${String(m).padStart(2, '0')}-${lastDay}`;
    }
    const threshold = getIndicatorThreshold(transportDisruptionsIndicatorName);
    const trendData = await fetchTransportDisruptionsTrendData(dateRangeStart, dateRangeEnd, wardNumber, threshold, city);

    const dateForTitle = startDate || selectedDate || new Date(2025, 4, 1);
    const monthName = dateForTitle.toLocaleDateString('en-US', { month: 'long' });
    const year = dateForTitle.getFullYear();
    const month = dateForTitle.getMonth();
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

    let dates = [];
    let paramValues = [];
    let dateLabels = [];
    let formattedDates = [];
    const isCommuters = transportDisruptionsMode === 'commuters_affected';

    if (trendData && trendData.dates && trendData.dates.length > 0) {
      dates = trendData.dates;
      paramValues = isCommuters
        ? (trendData.commuters_affected || []).map(v => v != null ? v : 0)
        : (trendData.disruption_minutes || []).map(v => v != null ? v : 0);
      dateLabels = dates.map(d => new Date(d).getDate());
      formattedDates = dates.map(d => new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
    } else {
      setError('Transport disruption trend data is not available');
    }

    const tempDates = [];
    let startDateForTemp = startDate || (selectedDate ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1) : new Date(2025, 4, 1));
    let endDateForTemp = endDate || (selectedDate ? new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0) : new Date(2025, 4, 31));
    startDateForTemp = new Date(startDateForTemp.getFullYear(), startDateForTemp.getMonth(), startDateForTemp.getDate());
    endDateForTemp = new Date(endDateForTemp.getFullYear(), endDateForTemp.getMonth(), endDateForTemp.getDate());
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (endDateForTemp > today) endDateForTemp = today;
    const currentDate = new Date(startDateForTemp);
    const endDateTimestamp = endDateForTemp.getTime();
    while (currentDate.getTime() <= endDateTimestamp) {
      tempDates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const maxTemperatureData = {};
    const wardNumForTemp = wardNumber || 1;
    const tempPromises = tempDates.map(date => fetchWeatherForDate(wardNumForTemp, date, city));
    const tempResults = await Promise.all(tempPromises);
    tempResults.forEach((result, idx) => {
      if (result && !result.error && result.max_temp_c != null) {
        maxTemperatureData[tempDates[idx]] = result.max_temp_c;
      }
    });
    // Fallback: if no ward-level temperature data, try city-level (last 30 days) for overlapping dates
    if (Object.keys(maxTemperatureData).length === 0 && city) {
      const cityWeather = await fetchCityLevelWeatherData(city);
      if (cityWeather && Array.isArray(cityWeather)) {
        cityWeather.forEach((item) => {
          if (item.date && (item.max_temp_c != null || item.avg_temp_c != null)) {
            maxTemperatureData[item.date] = item.max_temp_c != null ? item.max_temp_c : item.avg_temp_c;
          }
        });
      }
    }

    const allDaysInMonth = [];
    const allDayLabels = [];
    const allFormattedDates = [];
    for (let day = 1; day <= lastDayOfMonth; day++) {
      const fullDate = new Date(year, month, day);
      allDaysInMonth.push(fullDate.toISOString().split('T')[0]);
      allDayLabels.push(day);
      allFormattedDates.push(fullDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
    }

    const allTempValues = allDaysInMonth.map(d => maxTemperatureData[d]).filter(v => v != null);
    const tempMax = allTempValues.length > 0 ? Math.ceil(Math.max(...allTempValues)) : 40;
    const paramMax = paramValues.length > 0 ? Math.max(...paramValues) : 1;
    const barTitle = isCommuters ? 'Commuters Affected' : 'Disruption Minutes';
    const y2Label = isCommuters ? 'Commuters Affected' : 'Disruption Minutes (minutes)';

    const traces = [];
    if (paramValues.length > 0 && dateLabels.length > 0) {
      traces.push({
        x: dateLabels,
        y: paramValues,
        type: 'bar',
        name: barTitle,
        yaxis: 'y2',
        marker: { color: '#1976d2', opacity: 0.8 },
        customdata: formattedDates,
        hovertemplate: `<b>DATE:</b> %{customdata}<br><b>${barTitle}:</b> %{y:,.0f}<extra></extra>`,
        width: 0.6
      });
    }
    // Always add temperature trace so temperature trend is visible (use nulls when no data)
    const maxTempValuesForTrace = allDaysInMonth.map(d => maxTemperatureData[d] ?? null);
    const hasAnyTemp = maxTempValuesForTrace.some(v => v != null);
    traces.push({
      x: allDayLabels,
      y: maxTempValuesForTrace,
      type: 'scatter',
      mode: hasAnyTemp ? 'lines+markers' : 'lines',
      name: 'Max Temperature',
      yaxis: 'y',
      line: { color: '#f97316', width: 3 },
      marker: { size: hasAnyTemp ? 7 : 0, color: '#f97316' },
      connectgaps: false,
      customdata: allFormattedDates,
      hovertemplate: hasAnyTemp
        ? '<b>DATE:</b> %{customdata}<br><b>Maximum temperature:</b> %{y:.1f}°C<extra></extra>'
        : '<b>DATE:</b> %{customdata}<br><b>Maximum temperature:</b> No data<extra></extra>'
    });

    const transportTabName = isCommuters ? 'Commuters Affected' : 'Disruption Minutes';
    const transportTitle = wardNumber
      ? `Ward ${wardNumber} - ${transportTabName} - ${monthName} ${year}`
      : `City-Level ${transportTabName} - ${monthName} ${year}`;
    const layout = {
      title: { text: transportTitle, font: { size: 14 } },
      xaxis: {
        title: 'Day of month',
        tickmode: 'linear',
        tick0: 1,
        dtick: 2,
        range: [0.5, lastDayOfMonth + 0.5]
      },
      yaxis: {
        title: { text: 'Max Temperature (°C)', standoff: 20, font: { color: '#f97316', size: 12 } },
        tickfont: { color: '#f97316', size: 10 },
        side: 'left',
        showgrid: true,
        gridcolor: '#e5e7eb',
        zeroline: false,
        range: hasAnyTemp ? [0, tempMax] : [0, 40],
        automargin: true,
        visible: true
      },
      yaxis2: {
        title: { text: y2Label, standoff: 20, font: { color: '#1976d2', size: 12 } },
        tickfont: { color: '#1976d2', size: 10 },
        overlaying: 'y',
        side: 'right',
        showgrid: false,
        zeroline: false,
        range: [0, paramMax],
        automargin: true
      },
      margin: { t: 70, b: 50, l: 60, r: 60, pad: 5 },
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 350 : 400),
      showlegend: true,
      legend: { x: 0.5, y: -0.18, xanchor: 'center', orientation: 'h', bgcolor: 'rgba(255,255,255,0.8)' },
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      autosize: true,
      hovermode: 'closest',
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 } }
    };

    setPlotData(traces);
    setPlotLayout(layout);
  }, [startDateKey, endDateKey, selectedDateKey, selectedWard, transportDisruptionsMode, city, getIndicatorThreshold]);

  // Parameter-tab trend for the 10 indicators: fetch trend when available and show bar + temperature; else temperature only
  const renderTemperatureOnlyTrendForOtherIndicators = useCallback(async () => {
    let indicatorKey = '';
    let mode = '';
    let indicatorDisplayName = '';
    let parameterTabLabel = '';
    if (isCoolingSystemFailures) {
      indicatorKey = 'coolingSystem';
      mode = coolingSystemFailuresMode;
      indicatorDisplayName = INDICATOR_NAMES.coolingSystem;
      parameterTabLabel = PARAM_TAB_LABELS.coolingSystem[mode] || mode;
    } else if (isSchoolClosures) {
      indicatorKey = 'schoolClosures';
      mode = schoolClosuresMode;
      indicatorDisplayName = INDICATOR_NAMES.schoolClosures;
      parameterTabLabel = PARAM_TAB_LABELS.schoolClosures[mode] || mode;
    } else if (isSchoolCooling) {
      indicatorKey = 'schoolCooling';
      mode = schoolCoolingMode;
      indicatorDisplayName = INDICATOR_NAMES.schoolCooling;
      parameterTabLabel = PARAM_TAB_LABELS.schoolCooling[mode] || mode;
    } else if (isWaterSupply) {
      indicatorKey = 'waterSupply';
      mode = waterSupplyMode;
      indicatorDisplayName = INDICATOR_NAMES.waterSupply;
      parameterTabLabel = PARAM_TAB_LABELS.waterSupply[mode] || mode;
    } else if (isWaterTankers) {
      indicatorKey = 'waterTankers';
      mode = waterTankersMode;
      indicatorDisplayName = INDICATOR_NAMES.waterTankers;
      parameterTabLabel = PARAM_TAB_LABELS.waterTankers[mode] || mode;
    } else if (isHeatDifferential) {
      indicatorKey = 'heatDifferential';
      mode = heatDifferentialMode;
      indicatorDisplayName = INDICATOR_NAMES.heatDifferential;
      parameterTabLabel = PARAM_TAB_LABELS.heatDifferential[mode] || mode;
    } else if (isSurfaceTemperature) {
      indicatorKey = 'surfaceTemperature';
      mode = surfaceTemperatureMode;
      indicatorDisplayName = INDICATOR_NAMES.surfaceTemperature;
      parameterTabLabel = PARAM_TAB_LABELS.surfaceTemperature[mode] || mode;
    } else if (isShelterOccupancy) {
      indicatorKey = 'shelterOccupancy';
      mode = shelterOccupancyMode;
      indicatorDisplayName = INDICATOR_NAMES.shelterOccupancy;
      parameterTabLabel = PARAM_TAB_LABELS.shelterOccupancy[mode] || mode;
    } else if (isRoadClosures) {
      indicatorDisplayName = INDICATOR_NAMES.roadClosures;
      parameterTabLabel = roadClosuresMode;
    } else if (isTrafficSignalFailures) {
      indicatorDisplayName = INDICATOR_NAMES.trafficSignal;
      parameterTabLabel = trafficSignalFailuresMode;
    }
    if (!indicatorDisplayName) indicatorDisplayName = indicatorName || 'Indicator';

    const dateForTitle = startDate || selectedDate || new Date(2025, 4, 1);
    const monthName = dateForTitle.toLocaleDateString('en-US', { month: 'long' });
    const year = dateForTitle.getFullYear();
    const month = dateForTitle.getMonth();
    const lastDayOfMonth = new Date(year, month + 1, 0).getDate();

    let dateRangeStart = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    let dateRangeEnd = `${year}-${String(month + 1).padStart(2, '0')}-${lastDayOfMonth}`;
    if (startDate && endDate) {
      dateRangeStart = `${startDate.getFullYear()}-${String(startDate.getMonth() + 1).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;
      dateRangeEnd = `${endDate.getFullYear()}-${String(endDate.getMonth() + 1).padStart(2, '0')}-${String(endDate.getDate()).padStart(2, '0')}`;
    }

    const wardNumber = selectedWard ? selectedWard.id : null;
    let trendData = null;
    let paramConfig = null;
    if (indicatorKey && mode && PARAM_TREND_CONFIG[indicatorKey] && PARAM_TREND_CONFIG[indicatorKey][mode]) {
      paramConfig = PARAM_TREND_CONFIG[indicatorKey][mode];
      const threshold = getIndicatorThreshold(INDICATOR_NAMES[indicatorKey]);
      if (paramConfig.fetchFn) {
        try {
          trendData = await paramConfig.fetchFn(dateRangeStart, dateRangeEnd, wardNumber, threshold, city);
        } catch (e) {
          trendData = null;
        }
      }
    }

    let dateLabels = [];
    let paramValues = [];
    let formattedDates = [];
    const hasParamTrend = trendData && trendData.dates && trendData.dates.length > 0 && paramConfig && paramConfig.trendField && (trendData[paramConfig.trendField] || []).length > 0;
    if (hasParamTrend) {
      const rawValues = trendData[paramConfig.trendField];
      paramValues = (rawValues || []).map(v => {
        const n = typeof v === 'number' ? v : Number(v);
        return (v != null && !isNaN(n)) ? n : 0;
      });
      dateLabels = trendData.dates.map(d => new Date(d).getDate());
      formattedDates = trendData.dates.map(d => new Date(d).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
    }

    const tempDates = [];
    let startDateForTemp = startDate || (selectedDate ? new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1) : new Date(2025, 4, 1));
    let endDateForTemp = endDate || (selectedDate ? new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0) : new Date(2025, 4, 31));
    startDateForTemp = new Date(startDateForTemp.getFullYear(), startDateForTemp.getMonth(), startDateForTemp.getDate());
    endDateForTemp = new Date(endDateForTemp.getFullYear(), endDateForTemp.getMonth(), endDateForTemp.getDate());
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (endDateForTemp > today) endDateForTemp = today;
    const currentDate = new Date(startDateForTemp);
    const endDateTimestamp = endDateForTemp.getTime();
    while (currentDate.getTime() <= endDateTimestamp) {
      tempDates.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const maxTemperatureData = {};
    const wardNumForTemp = wardNumber || 1;
    const tempPromises = tempDates.map(date => fetchWeatherForDate(wardNumForTemp, date, city));
    const tempResults = await Promise.all(tempPromises);
    tempResults.forEach((result, idx) => {
      if (result && !result.error && result.max_temp_c != null) {
        maxTemperatureData[tempDates[idx]] = result.max_temp_c;
      }
    });
    if (Object.keys(maxTemperatureData).length === 0 && city) {
      const cityWeather = await fetchCityLevelWeatherData(city);
      if (cityWeather && Array.isArray(cityWeather)) {
        cityWeather.forEach((item) => {
          if (item.date && (item.max_temp_c != null || item.avg_temp_c != null)) {
            maxTemperatureData[item.date] = item.max_temp_c != null ? item.max_temp_c : item.avg_temp_c;
          }
        });
      }
    }

    const allDaysInMonth = [];
    const allDayLabels = [];
    const allFormattedDates = [];
    for (let day = 1; day <= lastDayOfMonth; day++) {
      const fullDate = new Date(year, month, day);
      allDaysInMonth.push(fullDate.toISOString().split('T')[0]);
      allDayLabels.push(day);
      allFormattedDates.push(fullDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }));
    }

    const maxTempValuesForTrace = allDaysInMonth.map(d => maxTemperatureData[d] ?? null);
    const hasAnyTemp = maxTempValuesForTrace.some(v => v != null);
    const allTempValues = maxTempValuesForTrace.filter(v => v != null);
    const tempMax = allTempValues.length > 0 ? Math.ceil(Math.max(...allTempValues)) : 40;
    const paramMax = hasParamTrend && paramValues.length > 0 ? Math.max(...paramValues, 1) : 1;

    const traces = [];
    if (hasParamTrend && dateLabels.length > 0 && paramConfig) {
      traces.push({
        x: dateLabels,
        y: paramValues,
        type: 'bar',
        name: paramConfig.barTitle,
        yaxis: 'y2',
        marker: { color: '#1976d2', opacity: 0.8 },
        customdata: formattedDates,
        hovertemplate: `<b>DATE:</b> %{customdata}<br><b>${paramConfig.barTitle}:</b> %{y:,.2f}<extra></extra>`,
        width: 0.6
      });
    }
    traces.push({
      x: allDayLabels,
      y: maxTempValuesForTrace,
      type: 'scatter',
      mode: hasAnyTemp ? 'lines+markers' : 'lines',
      name: 'Max Temperature',
      yaxis: traces.length > 1 ? 'y' : undefined,
      line: { color: '#f97316', width: 3 },
      marker: { size: hasAnyTemp ? 7 : 0, color: '#f97316' },
      connectgaps: false,
      customdata: allFormattedDates,
      hovertemplate: hasAnyTemp
        ? '<b>DATE:</b> %{customdata}<br><b>Maximum temperature:</b> %{y:.1f}°C<extra></extra>'
        : '<b>DATE:</b> %{customdata}<br><b>Maximum temperature:</b> No data<extra></extra>'
    });

    const otherIndicatorTitle = wardNumber
      ? `Ward ${wardNumber} - ${parameterTabLabel} - ${monthName} ${year}`
      : `City-Level ${parameterTabLabel} - ${monthName} ${year}`;
    const layout = {
      title: { text: otherIndicatorTitle, font: { size: 14 } },
      xaxis: {
        title: 'Day of month',
        tickmode: 'linear',
        tick0: 1,
        dtick: 2,
        range: [0.5, lastDayOfMonth + 0.5]
      },
      yaxis: {
        title: { text: 'Max Temperature (°C)', standoff: 20, font: { color: '#f97316', size: 12 } },
        tickfont: { color: '#f97316', size: 10 },
        side: 'left',
        showgrid: true,
        gridcolor: '#e5e7eb',
        zeroline: false,
        range: [0, tempMax],
        automargin: true
      },
      ...(hasParamTrend && paramConfig ? {
        yaxis2: {
          title: { text: paramConfig.y2Label, standoff: 20, font: { color: '#1976d2', size: 12 } },
          tickfont: { color: '#1976d2', size: 10 },
          overlaying: 'y',
          side: 'right',
          showgrid: false,
          zeroline: false,
          range: [0, paramMax],
          automargin: true
        }
      } : {}),
      margin: { t: 70, b: 50, l: 60, r: 60, pad: 5 },
      height: window.innerWidth <= 480 ? 300 : (window.innerWidth <= 768 ? 350 : 400),
      showlegend: true,
      legend: { x: 0.5, y: -0.18, xanchor: 'center', orientation: 'h', bgcolor: 'rgba(255,255,255,0.8)' },
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      autosize: true,
      hovermode: 'closest',
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 } }
    };

    setPlotData(traces);
    setPlotLayout(layout);
    setError(hasParamTrend ? null : 'Parameter trend data is not available; temperature trend shown.');
  }, [
    startDateKey, endDateKey, selectedDateKey, selectedWard, city,
    isRoadClosures, roadClosuresMode, isTrafficSignalFailures, trafficSignalFailuresMode,
    isCoolingSystemFailures, coolingSystemFailuresMode, isSchoolClosures, schoolClosuresMode,
    isSchoolCooling, schoolCoolingMode, isWaterSupply, waterSupplyMode, isWaterTankers, waterTankersMode,
    isHeatDifferential, heatDifferentialMode, isSurfaceTemperature, surfaceTemperatureMode,
    isShelterOccupancy, shelterOccupancyMode, indicatorName, getIndicatorThreshold
  ]);

  // Load and render trend chart
  const loadTrendData = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    
    const otherIndicatorNonSeverity = (isRoadClosures && roadClosuresMode !== 'severity') || (isTrafficSignalFailures && trafficSignalFailuresMode !== 'severity') || (isCoolingSystemFailures && coolingSystemFailuresMode !== 'severity') || (isSchoolClosures && schoolClosuresMode !== 'severity') || (isSchoolCooling && schoolCoolingMode !== 'severity') || (isWaterSupply && waterSupplyMode !== 'severity') || (isWaterTankers && waterTankersMode !== 'severity') || (isHeatDifferential && heatDifferentialMode !== 'severity') || (isSurfaceTemperature && surfaceTemperatureMode !== 'severity') || (isShelterOccupancy && shelterOccupancyMode !== 'severity');
    if ((isPowerOutage && powerOutageMode !== 'severity') || (isGroundwater && groundwaterMode !== 'severity') || (isInformalWorkers && informalWorkersMode !== 'severity') || (isConstructionLabor && constructionLaborMode !== 'severity') || (isTransportDisruptions && transportDisruptionsMode !== 'severity') || otherIndicatorNonSeverity) {
      setPlotData([]);
      setPlotLayout({});
    }

    try {
      // Severity pie data for the 10 indicators (same style as transport)
      if (isRoadClosures && roadClosuresMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
          const currentData = await fetchSeverityFromDataForWards(dateForFetch, fetchRoadClosuresDataForWards, 'roadClosures');
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(dateForFetch);
          previousYear.setFullYear(previousYear.getFullYear() - 1);
          const previousData = await fetchSeverityFromDataForWards(previousYear, fetchRoadClosuresDataForWards, 'roadClosures');
          setPreviousYearData(previousData || emptyData);
        } catch (e) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${e.message}. Showing empty charts.`);
        }
        setIsLoading(false);
        return;
      }
      if (isTrafficSignalFailures && trafficSignalFailuresMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
          const currentData = await fetchSeverityFromDataForWards(dateForFetch, fetchTrafficSignalFailuresDataForWards, 'trafficSignal');
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(dateForFetch);
          previousYear.setFullYear(previousYear.getFullYear() - 1);
          const previousData = await fetchSeverityFromDataForWards(previousYear, fetchTrafficSignalFailuresDataForWards, 'trafficSignal');
          setPreviousYearData(previousData || emptyData);
        } catch (e) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${e.message}. Showing empty charts.`);
        }
        setIsLoading(false);
        return;
      }
      if (isCoolingSystemFailures && coolingSystemFailuresMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
          const currentData = await fetchSeverityFromDataForWards(dateForFetch, fetchCoolingSystemFailuresDataForWards, 'coolingSystem');
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(dateForFetch);
          previousYear.setFullYear(previousYear.getFullYear() - 1);
          const previousData = await fetchSeverityFromDataForWards(previousYear, fetchCoolingSystemFailuresDataForWards, 'coolingSystem');
          setPreviousYearData(previousData || emptyData);
        } catch (e) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${e.message}. Showing empty charts.`);
        }
        setIsLoading(false);
        return;
      }
      if (isSchoolClosures && schoolClosuresMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
          const currentData = await fetchSeverityFromDataForWards(dateForFetch, fetchSchoolClosuresDataForWards, 'schoolClosures');
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(dateForFetch);
          previousYear.setFullYear(previousYear.getFullYear() - 1);
          const previousData = await fetchSeverityFromDataForWards(previousYear, fetchSchoolClosuresDataForWards, 'schoolClosures');
          setPreviousYearData(previousData || emptyData);
        } catch (e) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${e.message}. Showing empty charts.`);
        }
        setIsLoading(false);
        return;
      }
      if (isSchoolCooling && schoolCoolingMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
          const currentData = await fetchSeverityFromDataForWards(dateForFetch, fetchSchoolCoolingDataForWards, 'schoolCooling');
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(dateForFetch);
          previousYear.setFullYear(previousYear.getFullYear() - 1);
          const previousData = await fetchSeverityFromDataForWards(previousYear, fetchSchoolCoolingDataForWards, 'schoolCooling');
          setPreviousYearData(previousData || emptyData);
        } catch (e) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${e.message}. Showing empty charts.`);
        }
        setIsLoading(false);
        return;
      }
      if (isWaterSupply && waterSupplyMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
          const currentData = await fetchSeverityFromDataForWards(dateForFetch, fetchWaterSupplyDataForWards, 'waterSupply');
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(dateForFetch);
          previousYear.setFullYear(previousYear.getFullYear() - 1);
          const previousData = await fetchSeverityFromDataForWards(previousYear, fetchWaterSupplyDataForWards, 'waterSupply');
          setPreviousYearData(previousData || emptyData);
        } catch (e) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${e.message}. Showing empty charts.`);
        }
        setIsLoading(false);
        return;
      }
      if (isWaterTankers && waterTankersMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
          const currentData = await fetchSeverityFromDataForWards(dateForFetch, fetchWaterTankersDataForWards, 'waterTankers');
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(dateForFetch);
          previousYear.setFullYear(previousYear.getFullYear() - 1);
          const previousData = await fetchSeverityFromDataForWards(previousYear, fetchWaterTankersDataForWards, 'waterTankers');
          setPreviousYearData(previousData || emptyData);
        } catch (e) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${e.message}. Showing empty charts.`);
        }
        setIsLoading(false);
        return;
      }
      if (isHeatDifferential && heatDifferentialMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
          const currentData = await fetchSeverityFromDataForWards(dateForFetch, fetchHeatDifferentialDataForWards, 'heatDifferential');
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(dateForFetch);
          previousYear.setFullYear(previousYear.getFullYear() - 1);
          const previousData = await fetchSeverityFromDataForWards(previousYear, fetchHeatDifferentialDataForWards, 'heatDifferential');
          setPreviousYearData(previousData || emptyData);
        } catch (e) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${e.message}. Showing empty charts.`);
        }
        setIsLoading(false);
        return;
      }
      if (isSurfaceTemperature && surfaceTemperatureMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
          const currentData = await fetchSeverityFromDataForWards(dateForFetch, fetchSurfaceTemperatureDataForWards, 'surfaceTemperature');
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(dateForFetch);
          previousYear.setFullYear(previousYear.getFullYear() - 1);
          const previousData = await fetchSeverityFromDataForWards(previousYear, fetchSurfaceTemperatureDataForWards, 'surfaceTemperature');
          setPreviousYearData(previousData || emptyData);
        } catch (e) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${e.message}. Showing empty charts.`);
        }
        setIsLoading(false);
        return;
      }
      if (isShelterOccupancy && shelterOccupancyMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
          const currentData = await fetchSeverityFromDataForWards(dateForFetch, fetchShelterOccupancyDataForWards, 'shelterOccupancy');
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(dateForFetch);
          previousYear.setFullYear(previousYear.getFullYear() - 1);
          const previousData = await fetchSeverityFromDataForWards(previousYear, fetchShelterOccupancyDataForWards, 'shelterOccupancy');
          setPreviousYearData(previousData || emptyData);
        } catch (e) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${e.message}. Showing empty charts.`);
        }
        setIsLoading(false);
        return;
      }
      // Parameter tabs for the 10 indicators: show temperature trend (parameter trend API not available)
      if (otherIndicatorNonSeverity) {
        try {
          await renderTemperatureOnlyTrendForOtherIndicators();
        } catch (err) {
          setPlotData([]);
          setPlotLayout({});
          setError(`Trend data is not available for this parameter. ${err.message || ''}`);
        }
        setIsLoading(false);
        return;
      }
      if (isTransportDisruptions) {
        if (transportDisruptionsMode === 'severity') {
          const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          try {
            const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
            const currentData = await fetchTransportDisruptionsSeverityData(dateForFetch);
            setCurrentMonthData(currentData || emptyData);
            const previousYear = new Date(dateForFetch);
            previousYear.setFullYear(previousYear.getFullYear() - 1);
            const previousData = await fetchTransportDisruptionsSeverityData(previousYear);
            setPreviousYearData(previousData || emptyData);
          } catch (fetchError) {
            setCurrentMonthData(emptyData);
            setPreviousYearData(emptyData);
            setError(`API Error: ${fetchError.message}. Showing empty charts.`);
          }
        } else {
          try {
            await renderTransportDisruptionsTrend();
          } catch (trendError) {
            setPlotData([]);
            setError(`API Error: ${trendError.message}. Showing empty chart.`);
          }
        }
        setIsLoading(false);
        return;
      }
      if (isConstructionLabor) {
        try {
          await renderConstructionLaborTrend();
        } catch (trendError) {
          setPlotData([]);
          setError(`API Error: ${trendError.message}. Showing empty chart.`);
        }
        setIsLoading(false);
        return;
      } else if (isInformalWorkers) {
        try {
          await renderInformalWorkersTrend();
        } catch (trendError) {
          setPlotData([]);
          setError(`API Error: ${trendError.message}. Showing empty chart.`);
        }
      } else if (isGroundwater) {
        if (groundwaterMode === 'severity') {
          const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          try {
            const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
            const currentData = await fetchGroundwaterSeverityData(dateForFetch);
            setCurrentMonthData(currentData || emptyData);
            const previousYear = new Date(dateForFetch);
            previousYear.setFullYear(previousYear.getFullYear() - 1);
            const previousData = await fetchGroundwaterSeverityData(previousYear);
            setPreviousYearData(previousData || emptyData);
          } catch (fetchError) {
            setCurrentMonthData(emptyData);
            setPreviousYearData(emptyData);
            setError(`API Error: ${fetchError.message}. Showing empty charts.`);
          }
        } else {
          try {
            await renderGroundwaterTrend();
          } catch (trendError) {
            setPlotData([]);
            setError(`API Error: ${trendError.message}. Showing empty chart.`);
          }
        }
        setIsLoading(false);
        return;
      }

      if (!isPowerOutage && !isGroundwater && !isInformalWorkers && !isConstructionLabor && !isTransportDisruptions && !isRoadClosures && !isTrafficSignalFailures && !isCoolingSystemFailures && !isSchoolClosures && !isSchoolCooling && !isWaterSupply && !isWaterTankers && !isHeatDifferential && !isSurfaceTemperature && !isShelterOccupancy) {
        setError('Trend data is not available for this indicator');
        setIsLoading(false);
        return;
      }

      if (isPowerOutage && powerOutageMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const currentData = await fetchSeverityData(startDate);
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(startDate);
          previousYear.setFullYear(startDate.getFullYear() - 1);
          const previousData = await fetchSeverityData(previousYear);
          setPreviousYearData(previousData || emptyData);
        } catch (fetchError) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${fetchError.message}. Showing empty charts.`);
        }
      } else if (isPowerOutage) {
        try {
          await renderPowerOutageTrend();
        } catch (trendError) {
          setPlotData([]);
          setError(`API Error: ${trendError.message}. Showing empty chart.`);
        }
      }
    } catch (err) {
      if (isPowerOutage && powerOutageMode === 'severity') {
        setCurrentMonthData({ severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 });
        setPreviousYearData({ severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 });
      } else if (isGroundwater && groundwaterMode === 'severity') {
        setCurrentMonthData({ severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 });
        setPreviousYearData({ severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 });
      } else if (isInformalWorkers && informalWorkersMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const dateForFetch = startDate || selectedDate || new Date(2026, 0, 1);
          const currentData = await fetchInformalWorkersSeverityData(dateForFetch);
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(dateForFetch);
          previousYear.setFullYear(previousYear.getFullYear() - 1);
          const previousData = await fetchInformalWorkersSeverityData(previousYear);
          setPreviousYearData(previousData || emptyData);
        } catch (fetchError) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${fetchError.message}. Showing empty charts.`);
        }
      } else if (isConstructionLabor && constructionLaborMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
          const currentData = await fetchConstructionLaborSeverityData(dateForFetch);
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(dateForFetch);
          previousYear.setFullYear(previousYear.getFullYear() - 1);
          const previousData = await fetchConstructionLaborSeverityData(previousYear);
          setPreviousYearData(previousData || emptyData);
        } catch (fetchError) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${fetchError.message}. Showing empty charts.`);
        }
      } else if (isTransportDisruptions && transportDisruptionsMode === 'severity') {
        const emptyData = { severityCounts: { LOW: 0, MEDIUM: 0, HIGH: 0 }, totalWards: 0 };
        setCurrentMonthData(emptyData);
        setPreviousYearData(emptyData);
        try {
          const dateForFetch = startDate || selectedDate || new Date(2025, 4, 1);
          const currentData = await fetchTransportDisruptionsSeverityData(dateForFetch);
          setCurrentMonthData(currentData || emptyData);
          const previousYear = new Date(dateForFetch);
          previousYear.setFullYear(previousYear.getFullYear() - 1);
          const previousData = await fetchTransportDisruptionsSeverityData(previousYear);
          setPreviousYearData(previousData || emptyData);
        } catch (fetchError) {
          setCurrentMonthData(emptyData);
          setPreviousYearData(emptyData);
          setError(`API Error: ${fetchError.message}. Showing empty charts.`);
        }
      } else {
        setPlotData([]);
      }
      setError(`Error: ${err.message}. Showing empty chart.`);
    } finally {
      setIsLoading(false);
    }
  }, [isPowerOutage, powerOutageMode, isGroundwater, groundwaterMode, isInformalWorkers, informalWorkersMode, isConstructionLabor, constructionLaborMode, isTransportDisruptions, transportDisruptionsMode, isRoadClosures, roadClosuresMode, isTrafficSignalFailures, trafficSignalFailuresMode, isCoolingSystemFailures, coolingSystemFailuresMode, isSchoolClosures, schoolClosuresMode, isSchoolCooling, schoolCoolingMode, isWaterSupply, waterSupplyMode, isWaterTankers, waterTankersMode, isHeatDifferential, heatDifferentialMode, isSurfaceTemperature, surfaceTemperatureMode, isShelterOccupancy, shelterOccupancyMode, selectedWard, city, startDateKey, endDateKey, selectedDateKey, fetchSeverityData, fetchGroundwaterSeverityData, fetchInformalWorkersSeverityData, fetchConstructionLaborSeverityData, fetchTransportDisruptionsSeverityData, fetchSeverityFromDataForWards, renderPowerOutageTrend, renderGroundwaterTrend, renderInformalWorkersTrend, renderConstructionLaborTrend, renderTransportDisruptionsTrend, renderTemperatureOnlyTrendForOtherIndicators]);

  // Render temperature trend
  const renderTemperatureTrend = async () => {
    let data;
    
    if (selectedWard) {
      data = await fetchWeatherDataForWard(selectedWard, city);
    } else {
      data = await fetchCityLevelWeatherData(city);
    }

    if (!data || data.length === 0) {
      setError('Weather data is not available');
      return;
    }

    setWeatherData(data);

    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const dates = data.map(d => {
      const date = new Date(d.date);
      return `${monthNames[date.getMonth()]} ${date.getDate()}`;
    });
    const maxTemps = data.map(d => d.max_temp_c);
    const minTemps = data.map(d => d.min_temp_c);
    const avgTemps = data.map(d => d.avg_temp_c);

    const chartTitle = selectedWard
      ? `Temperature Trends - ${selectedWard.name}`
      : `Temperature Trends - ${city} (City Average)`;

    const traces = [
      {
        x: dates,
        y: maxTemps,
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Max Temperature',
        line: { color: '#e74c3c', width: 3, shape: 'spline' },
        marker: { size: 6, color: '#e74c3c' },
        fill: 'none'
      },
      {
        x: dates,
        y: minTemps,
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Min Temperature',
        line: { color: '#3498db', width: 3, shape: 'spline' },
        marker: { size: 6, color: '#3498db' },
        fill: 'tonexty',
        fillcolor: 'rgba(52, 152, 219, 0.1)'
      },
      {
        x: dates,
        y: avgTemps,
        type: 'scatter',
        mode: 'lines+markers',
        name: 'Avg Temperature',
        line: { color: '#f39c12', width: 3, shape: 'spline', dash: 'dash' },
        marker: { size: 6, color: '#f39c12' },
        fill: 'none'
      }
    ];

    const layout = {
      title: {
        text: chartTitle,
        font: { size: window.innerWidth <= 480 ? 11 : (window.innerWidth <= 768 ? 12 : 13), color: '#2c3e50' },
        y: 0.98,
        yref: 'container',
        yanchor: 'top',
        pad: { t: 10 }
      },
      xaxis: {
        title: { text: 'Date (Last 30 Days)', standoff: 10 },
        gridcolor: '#e0e0e0',
        showgrid: true,
        tickangle: -45,
        tickmode: 'linear',
        tick0: 0,
        dtick: 5,
        showline: true,
        linecolor: '#333',
        mirror: false,
        showticklabels: true,
        tickfont: { size: 10 },
        side: 'bottom'
      },
      yaxis: {
        title: { text: 'Temperature (°C)', standoff: 20 },
        gridcolor: '#e0e0e0',
        showgrid: true,
        showline: true,
        linecolor: '#333',
        mirror: false
      },
      margin: { 
        t: window.innerWidth <= 480 ? 50 : 60, 
        b: window.innerWidth <= 480 ? 60 : 80, 
        l: window.innerWidth <= 480 ? 40 : 50, 
        r: window.innerWidth <= 480 ? 15 : 20 
      },
      plot_bgcolor: 'white',
      paper_bgcolor: 'white',
      hovermode: 'closest',
      hoverdistance: 5,
      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: 12 } },
      autosize: true,
      legend: {
        orientation: 'h',
        y: 1.75,
        x: 0.5,
        xanchor: 'center',
        yanchor: 'bottom'
      }
    };

    setPlotData(traces);
    setPlotLayout(layout);
  };

  // Listen for threshold changes
  useEffect(() => {
    const handleThresholdChange = () => {
      if ((isPowerOutage && powerOutageMode === 'severity') || (isGroundwater && groundwaterMode === 'severity') || (isInformalWorkers && informalWorkersMode === 'severity') || (isConstructionLabor && constructionLaborMode === 'severity') || (isTransportDisruptions && transportDisruptionsMode === 'severity') || (isRoadClosures && roadClosuresMode === 'severity') || (isTrafficSignalFailures && trafficSignalFailuresMode === 'severity') || (isCoolingSystemFailures && coolingSystemFailuresMode === 'severity') || (isSchoolClosures && schoolClosuresMode === 'severity') || (isSchoolCooling && schoolCoolingMode === 'severity') || (isWaterSupply && waterSupplyMode === 'severity') || (isWaterTankers && waterTankersMode === 'severity') || (isHeatDifferential && heatDifferentialMode === 'severity') || (isSurfaceTemperature && surfaceTemperatureMode === 'severity') || (isShelterOccupancy && shelterOccupancyMode === 'severity')) {
        loadTrendData();
      }
    };
    window.addEventListener('thresholdChanged', handleThresholdChange);
    return () => window.removeEventListener('thresholdChanged', handleThresholdChange);
  }, [isPowerOutage, powerOutageMode, isGroundwater, groundwaterMode, isInformalWorkers, informalWorkersMode, isConstructionLabor, constructionLaborMode, isTransportDisruptions, transportDisruptionsMode, isRoadClosures, roadClosuresMode, isTrafficSignalFailures, trafficSignalFailuresMode, isCoolingSystemFailures, coolingSystemFailuresMode, isSchoolClosures, schoolClosuresMode, isSchoolCooling, schoolCoolingMode, isWaterSupply, waterSupplyMode, isWaterTankers, waterTankersMode, isHeatDifferential, heatDifferentialMode, isSurfaceTemperature, surfaceTemperatureMode, isShelterOccupancy, shelterOccupancyMode, loadTrendData]);

  useEffect(() => {
    loadTrendData();
  }, [loadTrendData]);
  const showSeverityPies = (isPowerOutage && powerOutageMode === 'severity') || (isGroundwater && groundwaterMode === 'severity') || (isInformalWorkers && informalWorkersMode === 'severity') || (isConstructionLabor && constructionLaborMode === 'severity') || (isTransportDisruptions && transportDisruptionsMode === 'severity') || (isRoadClosures && roadClosuresMode === 'severity') || (isTrafficSignalFailures && trafficSignalFailuresMode === 'severity') || (isCoolingSystemFailures && coolingSystemFailuresMode === 'severity') || (isSchoolClosures && schoolClosuresMode === 'severity') || (isSchoolCooling && schoolCoolingMode === 'severity') || (isWaterSupply && waterSupplyMode === 'severity') || (isWaterTankers && waterTankersMode === 'severity') || (isHeatDifferential && heatDifferentialMode === 'severity') || (isSurfaceTemperature && surfaceTemperatureMode === 'severity') || (isShelterOccupancy && shelterOccupancyMode === 'severity');
  const severityChartTitle = isRoadClosures ? 'Road/bridge degradation or closures'
    : isTrafficSignalFailures ? 'Traffic signal / equipment failures'
    : isCoolingSystemFailures ? 'Cooling system failure'
    : isSchoolClosures ? 'School/college closure days'
    : isSchoolCooling ? 'Schools without cooling'
    : isWaterSupply ? 'Drop in water availability'
    : isWaterTankers ? 'Wards dependent on water tankers'
    : isHeatDifferential ? 'Temperature differential'
    : isSurfaceTemperature ? 'Surface temperature & vegetation'
    : isShelterOccupancy ? 'Homeless and street dwellers'
    : isTransportDisruptions
    ? 'Transport disruption'
    : isGroundwater
    ? 'Groundwater stress'
    : (isConstructionLabor
      ? (indicatorName === 'Lost labor hours due to heat stress' 
          ? 'Lost Labor Hours' 
          : indicatorName === '% of workers without access to shade/water'
          ? '% Without Shade/Water'
          : 'Heat Illness Cases')
      : (isInformalWorkers
        ? (indicatorName === 'No show rate among informal workers' ? 'No Show Rate' : 'Daily Wage Loss')
        : 'Unplanned Outage'));

  const severityLegend = (() => {
    if (!showSeverityPies) return null;
    if (isConstructionLabor) {
      let title = '';
      if (indicatorName === 'Lost labor hours due to heat stress') {
        title = 'Lost labor hours severity';
      } else if (indicatorName === '% of workers without access to shade/water') {
        title = '% without shade/water severity';
      } else {
        title = 'Heat illness cases severity';
      }
      return {
        title,
        low: 'LOW',
        med: 'MEDIUM',
        high: 'HIGH',
        none: 'No data'
      };
    } else if (isInformalWorkers) {
      const title =
        indicatorName === 'No show rate among informal workers'
          ? 'No show rate severity'
          : 'Daily wage loss severity';
      return {
        title,
        low: 'LOW',
        med: 'MEDIUM',
        high: 'HIGH',
        none: 'No data'
      };
    }
    if (isGroundwater) {
      return {
        title: 'Groundwater severity',
        low: 'LOW',
        med: 'MEDIUM',
        high: 'HIGH',
        none: 'No data'
      };
    }
    if (isTransportDisruptions) {
      return { title: 'Transport disruption severity', low: 'LOW', med: 'MEDIUM', high: 'HIGH', none: 'No data' };
    }
    if (isRoadClosures) return { title: 'Road/bridge severity', low: 'LOW', med: 'MEDIUM', high: 'HIGH', none: 'No data' };
    if (isTrafficSignalFailures) return { title: 'Traffic signal severity', low: 'LOW', med: 'MEDIUM', high: 'HIGH', none: 'No data' };
    if (isCoolingSystemFailures) return { title: 'Cooling system severity', low: 'LOW', med: 'MEDIUM', high: 'HIGH', none: 'No data' };
    if (isSchoolClosures) return { title: 'School closure severity', low: 'LOW', med: 'MEDIUM', high: 'HIGH', none: 'No data' };
    if (isSchoolCooling) return { title: 'School cooling severity', low: 'LOW', med: 'MEDIUM', high: 'HIGH', none: 'No data' };
    if (isWaterSupply) return { title: 'Water supply severity', low: 'LOW', med: 'MEDIUM', high: 'HIGH', none: 'No data' };
    if (isWaterTankers) return { title: 'Water tankers severity', low: 'LOW', med: 'MEDIUM', high: 'HIGH', none: 'No data' };
    if (isHeatDifferential) return { title: 'Heat differential severity', low: 'LOW', med: 'MEDIUM', high: 'HIGH', none: 'No data' };
    if (isSurfaceTemperature) return { title: 'Surface temperature severity', low: 'LOW', med: 'MEDIUM', high: 'HIGH', none: 'No data' };
    if (isShelterOccupancy) return { title: 'Shelter occupancy severity', low: 'LOW', med: 'MEDIUM', high: 'HIGH', none: 'No data' };
    // Power outage
    return {
      title: 'Power outage severity',
      low: 'LOW (Well within the threshold)',
      med: 'MEDIUM (About to cross the threshold)',
      high: 'HIGH (Crossed the threshold)',
      none: 'No Unplanned outage'
    };
  })();

  // Handle window resize for responsive charts
  useEffect(() => {
    const handleResize = () => {
      // Trigger Plotly to resize charts
      if (plotData.length > 0 && plotLayout) {
        // Plotly's responsive config should handle this, but we can force a resize
        const plotElements = document.querySelectorAll('.trend-container .js-plotly-plot');
        plotElements.forEach(el => {
          if (el) {
            Plotly.Plots.resize(el);
          }
        });
      }
    };

    let resizeTimeout;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', debouncedResize);
    };
  }, [plotData, plotLayout]);

  // Get latest weather data for display
  const latestWeather = weatherData && weatherData.length > 0 
    ? weatherData[weatherData.length - 1] 
    : null;
  
  // In severity pie mode we disable scrolling, so keep content compact
  const severityBoxHeight = window.innerWidth <= 480 ? 280 : (window.innerWidth <= 768 ? 300 : 280);
  const severityPlotHeight = window.innerWidth <= 480 ? 260 : (window.innerWidth <= 768 ? 280 : 260);

  return (
    <div className={`trend-container${showSeverityPies ? ' severity-mode' : ''}`} style={{ position: 'relative' }}>
      {selectedWard && (
        <div className="trend-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <div className="ward-selected">Ward: {selectedWard.name}</div>
            <button className="reset-ward-btn" onClick={handleResetWard}>
              Reset to City View
            </button>
          </div>
        </div>
      )}

      {/* Weather Info Section */}
      {/* Hide weather panel in severity pie mode so layout matches Power Outage and avoids scroll */}
      {!showSeverityPies && !isPowerOutage && latestWeather && (
        <div className="weather-info">
          <div className="weather-info-title">
            Weather Data - {selectedWard ? selectedWard.name : `${city} (City Average)`}
          </div>
          <div className="weather-stats-grid">
            <div className="weather-stat-item">
              <div className="weather-stat-label">Max Temperature</div>
              <div className="weather-stat-value">
                {latestWeather.max_temp_c !== null && latestWeather.max_temp_c !== undefined
                  ? latestWeather.max_temp_c.toFixed(1)
                  : 'N/A'}
                <span className="weather-stat-unit">°C</span>
              </div>
            </div>
            <div className="weather-stat-item">
              <div className="weather-stat-label">Min Temperature</div>
              <div className="weather-stat-value">
                {latestWeather.min_temp_c !== null && latestWeather.min_temp_c !== undefined
                  ? latestWeather.min_temp_c.toFixed(1)
                  : 'N/A'}
                <span className="weather-stat-unit">°C</span>
              </div>
            </div>
            <div className="weather-stat-item">
              <div className="weather-stat-label">Avg Temperature</div>
              <div className="weather-stat-value">
                {latestWeather.avg_temp_c !== null && latestWeather.avg_temp_c !== undefined
                  ? latestWeather.avg_temp_c.toFixed(1)
                  : 'N/A'}
                <span className="weather-stat-unit">°C</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSeverityPies ? (
        // Severity mode: Show two pie charts (power outage or groundwater)
        isLoading ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '320px',
            color: '#666',
            gap: '0.5rem',
            padding: '2rem'
          }}>
            <div>Loading severity data...</div>
            {error && <div style={{ fontSize: '0.85rem', color: '#c0392b', marginTop: '0.5rem', textAlign: 'center' }}>{error}</div>}
          </div>
        ) : (
          <>
            {error && (
              <div style={{ 
                background: '#fff3cd', 
                border: '1px solid #ffc107', 
                borderRadius: '4px', 
                padding: '0.5rem 0.75rem', 
                marginBottom: '0.5rem',
                fontSize: '0.75rem', 
                color: '#856404'
              }}>
                ⚠️ {error}
              </div>
            )}
            <div className="severity-charts-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.4rem', marginBottom: '0' }}>
              {/* Current Month Chart */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div className="severity-chart-container" style={{ background: 'white', borderRadius: '6px', border: '1px solid #dee2e6', height: `${severityBoxHeight}px` }}>
                  <Plot
                    data={[{
                      values: (() => {
                        const counts = currentMonthData?.severityCounts || { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 };
                        return [
                          counts.LOW || 0,
                          counts.MEDIUM || 0,
                          (counts.HIGH || 0) + (counts.CRITICAL || 0)
                        ];
                      })(),
                      labels: ['LOW', 'MEDIUM', 'HIGH'],
                      type: 'pie',
                      hole: 0.5,
                      marker: {
                        colors: ['#2E7D32', '#F9A825', '#c0392b'],
                        line: { color: '#fff', width: 2 }
                      },
                      textinfo: 'percent',
                      textposition: (() => {
                        const counts = currentMonthData?.severityCounts || { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 };
                        const highTotal = (counts.HIGH || 0) + (counts.CRITICAL || 0);
                        const total = (counts.LOW || 0) + (counts.MEDIUM || 0) + highTotal;
                        if (total === 0) return ['inside', 'inside', 'inside'];
                        return [
                          ((counts.LOW || 0) / total * 100) < 5 ? 'outside' : 'inside',
                          ((counts.MEDIUM || 0) / total * 100) < 5 ? 'outside' : 'inside',
                          (highTotal / total * 100) < 5 ? 'outside' : 'inside'
                        ];
                      })(),
                      textfont: (() => {
                        const counts = currentMonthData?.severityCounts || { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 };
                        const highTotal = (counts.HIGH || 0) + (counts.CRITICAL || 0);
                        const total = (counts.LOW || 0) + (counts.MEDIUM || 0) + highTotal;
                        if (total === 0) return { size: 12, color: ['#2E7D32', '#F9A825', '#c0392b'] };
                        return {
                          size: 12,
                          color: [
                            ((counts.LOW || 0) / total * 100) < 5 ? '#2E7D32' : 'white',
                            ((counts.MEDIUM || 0) / total * 100) < 5 ? '#F9A825' : 'white',
                            (highTotal / total * 100) < 5 ? '#c0392b' : 'white'
                          ]
                        };
                      })(),
                      hovertext: [
                        `<b>LOW</b><br>Total number of low wards: ${currentMonthData?.severityCounts?.LOW || 0}<br>Percentage of low wards: ${(currentMonthData?.totalWards || 0) > 0 ? (((currentMonthData?.severityCounts?.LOW || 0) / (currentMonthData?.totalWards || 1)) * 100).toFixed(1) : 0}%`,
                        `<b>MEDIUM</b><br>Total number of medium wards: ${currentMonthData?.severityCounts?.MEDIUM || 0}<br>Percentage of medium wards: ${(currentMonthData?.totalWards || 0) > 0 ? (((currentMonthData?.severityCounts?.MEDIUM || 0) / (currentMonthData?.totalWards || 1)) * 100).toFixed(1) : 0}%`,
                        `<b>HIGH</b><br>Total number of high wards: ${(currentMonthData?.severityCounts?.HIGH || 0) + (currentMonthData?.severityCounts?.CRITICAL || 0)}<br>Percentage of high wards: ${(currentMonthData?.totalWards || 0) > 0 ? ((((currentMonthData?.severityCounts?.HIGH || 0) + (currentMonthData?.severityCounts?.CRITICAL || 0)) / (currentMonthData?.totalWards || 1)) * 100).toFixed(1) : 0}%`
                      ],
                      hovertemplate: '%{hovertext}<extra></extra>'
                    }]}
                    layout={{
                      title: {
                        text: `<b>Current Period - ${startDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</b>`,
                        font: { size: window.innerWidth <= 480 ? 14 : 16 },
                        y: 0.95,
                        x: 0.5,
                        xanchor: 'center',
                        yanchor: 'top',
                        pad: { t: 10 }
                      },
                      height: severityPlotHeight,
                      margin: { 
                        t: window.innerWidth <= 480 ? 55 : 70, 
                        b: 15, 
                        l: window.innerWidth <= 480 ? 20 : 40, 
                        r: window.innerWidth <= 480 ? 20 : 40 
                      },
                      showlegend: false,
                      hovermode: 'closest',
                      hoverdistance: 5,
                      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: window.innerWidth <= 480 ? 10 : 12 } }
                    }}
                    config={{ responsive: true, displayModeBar: false }}
                    style={{ width: '100%', height: '100%' }}
                  />
                </div>
                <div style={{ textAlign: 'center', paddingTop: '0.5rem', fontSize: '0.75rem', color: '#495057', fontWeight: 'bold', }}>
                  {severityChartTitle} in {currentMonthData?.totalWards || 0} wards
                </div>
              </div>

              {/* Previous Year Chart */}
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                <div style={{ background: 'white', borderRadius: '6px', border: '1px solid #dee2e6', height: `${severityBoxHeight}px` }}>
                  <Plot
                    data={[{
                      values: (() => {
                        const counts = previousYearData?.severityCounts || { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 };
                        return [
                          counts.LOW || 0,
                          counts.MEDIUM || 0,
                          (counts.HIGH || 0) + (counts.CRITICAL || 0)
                        ];
                      })(),
                      labels: ['LOW', 'MEDIUM', 'HIGH'],
                      type: 'pie',
                      hole: 0.5,
                      marker: {
                        colors: ['#2E7D32', '#F9A825', '#c0392b'],
                        line: { color: '#fff', width: 2 }
                      },
                      textinfo: 'percent',
                      textposition: (() => {
                        const counts = previousYearData?.severityCounts || { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 };
                        const highTotal = (counts.HIGH || 0) + (counts.CRITICAL || 0);
                        const total = (counts.LOW || 0) + (counts.MEDIUM || 0) + highTotal;
                        if (total === 0) return ['inside', 'inside', 'inside'];
                        return [
                          ((counts.LOW || 0) / total * 100) < 5 ? 'outside' : 'inside',
                          ((counts.MEDIUM || 0) / total * 100) < 5 ? 'outside' : 'inside',
                          (highTotal / total * 100) < 5 ? 'outside' : 'inside'
                        ];
                      })(),
                      textfont: (() => {
                        const counts = previousYearData?.severityCounts || { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 };
                        const highTotal = (counts.HIGH || 0) + (counts.CRITICAL || 0);
                        const total = (counts.LOW || 0) + (counts.MEDIUM || 0) + highTotal;
                        if (total === 0) return { size: 12, color: ['#2E7D32', '#F9A825', '#B71C1C'] };
                        return {
                          size: 12,
                          color: [
                            ((counts.LOW || 0) / total * 100) < 5 ? '#2E7D32' : 'white',
                            ((counts.MEDIUM || 0) / total * 100) < 5 ? '#F9A825' : 'white',
                            (highTotal / total * 100) < 5 ? '#B71C1C' : 'white'
                          ]
                        };
                      })(),
                      hovertext: [
                        `<b>LOW</b><br>Total number of low wards: ${previousYearData?.severityCounts?.LOW || 0}<br>Percentage of low wards: ${(previousYearData?.totalWards || 0) > 0 ? (((previousYearData?.severityCounts?.LOW || 0) / (previousYearData?.totalWards || 1)) * 100).toFixed(1) : 0}%`,
                        `<b>MEDIUM</b><br>Total number of medium wards: ${previousYearData?.severityCounts?.MEDIUM || 0}<br>Percentage of medium wards: ${(previousYearData?.totalWards || 0) > 0 ? (((previousYearData?.severityCounts?.MEDIUM || 0) / (previousYearData?.totalWards || 1)) * 100).toFixed(1) : 0}%`,
                        `<b>HIGH</b><br>Total number of high wards: ${(previousYearData?.severityCounts?.HIGH || 0) + (previousYearData?.severityCounts?.CRITICAL || 0)}<br>Percentage of high wards: ${(previousYearData?.totalWards || 0) > 0 ? ((((previousYearData?.severityCounts?.HIGH || 0) + (previousYearData?.severityCounts?.CRITICAL || 0)) / (previousYearData?.totalWards || 1)) * 100).toFixed(1) : 0}%`
                      ],
                      hovertemplate: '%{hovertext}<extra></extra>'
                    }]}
                    layout={{
                      title: {
                        text: `<b>Previous Year - ${new Date(startDate.getFullYear() - 1, startDate.getMonth(), 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</b>`,
                        font: { size: window.innerWidth <= 480 ? 14 : 16 },
                        y: 0.95,
                        x: 0.5,
                        xanchor: 'center',
                        yanchor: 'top',
                        pad: { t: 10 }
                      },
                      height: severityPlotHeight,
                      margin: { 
                        t: window.innerWidth <= 480 ? 55 : 70, 
                        b: 15, 
                        l: window.innerWidth <= 480 ? 20 : 40, 
                        r: window.innerWidth <= 480 ? 20 : 40 
                      },
                      showlegend: false,
                      hovermode: 'closest',
                      hoverdistance: 5,
                      hoverlabel: { bgcolor: 'white', bordercolor: '#333', font: { size: window.innerWidth <= 480 ? 10 : 12 } }
                    }}
                    config={{ responsive: true, displayModeBar: false }}
                    style={{ width: '100%', height: '100%' }}
                  />
                </div>
                <div style={{ textAlign: 'center', paddingTop: '0.5rem', fontSize: '0.75rem', color: '#495057', fontWeight: 'bold',}}>
                  {severityChartTitle} in {previousYearData?.totalWards || 0} wards
                </div>
              </div>
            </div>
            
            {/* Legend below text (indicator-specific) */}
            <div className="severity-legend" style={{
              display: 'grid',
              gridTemplateColumns: 'auto 1fr auto 1fr',
              columnGap: '1.5rem',
              rowGap: '0.5rem',
              marginTop: '0.75rem',
              marginBottom: '0',
              padding: '0.4rem 1rem',
              background: '#f8f9fa',
              borderRadius: '6px',
              border: '1px solid #e9ecef',
              alignItems: 'center'
            }}>
              {severityLegend?.title && (
                <div style={{ gridColumn: '1 / -1', fontSize: '0.75rem', fontWeight: 700, color: '#2c3e50', marginBottom: '0.15rem' }}>
                  {severityLegend.title}
                </div>
              )}
              {/* First row */}
              <div style={{ width: '14px', height: '14px', background: '#2E7D32', borderRadius: '3px', border: '1px solid #ddd', flexShrink: 0 }} />
              <span style={{ fontSize: '0.7rem', color: '#495057', fontWeight: 500 }}>{severityLegend?.low || 'LOW'}</span>
              <div style={{ width: '14px', height: '14px', background: '#F9A825', borderRadius: '3px', border: '1px solid #ddd', flexShrink: 0 }} />
              <span style={{ fontSize: '0.7rem', color: '#495057', fontWeight: 500 }}>{severityLegend?.med || 'MEDIUM'}</span>
              {/* Second row */}
              <div style={{ width: '14px', height: '14px', background: '#c0392b', borderRadius: '3px', border: '1px solid #ddd', flexShrink: 0 }} />
              <span style={{ fontSize: '0.7rem', color: '#495057', fontWeight: 500 }}>{severityLegend?.high || 'HIGH'}</span>
              <div style={{ width: '14px', height: '14px', background: '#95a5a6', borderRadius: '3px', border: '1px solid #ddd', flexShrink: 0 }} />
              <span style={{ fontSize: '0.7rem', color: '#495057', fontWeight: 500 }}>{severityLegend?.none || 'No data'}</span>
            </div>
            
          </>
        )
      ) : (
        // Other modes: Show trend chart
        isLoading ? (
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            minHeight: '400px',
            color: '#666'
          }}>
            Loading trend data...
          </div>
        ) : (
          <div style={{ flex: 1, minHeight: '400px', display: 'flex', flexDirection: 'column', position: 'relative' }}>
            {error && (
              <div style={{ 
                position: 'absolute',
                top: '10px',
                left: '10px',
                right: '10px',
                background: '#fff3cd', 
                border: '1px solid #ffc107', 
                borderRadius: '4px', 
                padding: '0.5rem 0.75rem', 
                fontSize: '0.75rem', 
                color: '#856404',
                zIndex: 100
              }}>
                ⚠️ {error}
              </div>
            )}
            {plotData.length > 0 ? (
              <Plot
                data={plotData}
                layout={plotLayout}
                config={{ responsive: true, displayModeBar: false, autosize: true }}
                style={{ width: '100%', height: '100%', flex: 1 }}
              />
            ) : (
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                flex: 1,
                color: '#7f8c8d',
                fontSize: '0.9rem'
              }}>
                <div style={{ marginBottom: '0.5rem' }}>No trend data available</div>
                <div style={{ fontSize: '0.75rem', color: '#95a5a6' }}>Chart will appear when data is loaded</div>
              </div>
            )}
          </div>
        )
      )}

      {/* Date Range Selector - For all indicators with severity/parameter tabs */}
      {(isPowerOutage || isGroundwater || isInformalWorkers || isConstructionLabor || isTransportDisruptions || isRoadClosures || isTrafficSignalFailures || isCoolingSystemFailures || isSchoolClosures || isSchoolCooling || isWaterSupply || isWaterTankers || isHeatDifferential || isSurfaceTemperature || isShelterOccupancy) && availableMonths && (
        <DateRangeSelector
          startDate={startDate}
          endDate={endDate}
          onStartDateChange={onStartDateChange}
          onEndDateChange={onEndDateChange}
          availableMonths={availableMonths}
        />
      )}
    </div>
  );
};

export default TrendContainer;

